import React from 'react';
import {Button, Label} from 'reactstrap';
import {AvGroup} from 'availity-reactstrap-validation';
import Translate from '../../shared/language/Translate';
import GeneralUtils from '../../shared/utils/GeneralUtils';
import AvField from '../../shared/component/form/AvField';
import PurchaseRequisitionService from '../../shared/service/PurchaseRequisitionService';
import SupplierService from '../../shared/service/SupplierService';
import ProductService from '../../shared/service/ProductService';
import MerchantBranchSettingsService from '../../shared/service/MerchantBranchSettingsService';
import DepartmentService from '../../shared/service/DepartmentService';
import TaxService from '../../shared/service/TaxService';
import NumberFormat from 'react-number-format';
import {toast} from 'react-toastify';
import {
    PRODUCT_UNIT,
    RETURN_REASON_CODE,
    DELIVERY_WASTE_REASON_CODE,
    WEB_IMAGE_SERVER_URL,
    ORDER_UNIT_OF_MEASURE
} from '../../shared/utils/Constants';
import {hasSelectedSupplierOverride, normalizeItem} from '../purchaserequisition/PurchaseRequisitionUtils';
import {
    calculateActualOrderAmount,
    calculateOrderAmountFromBoxes,
    deriveOrderBoxCount,
    getActualOrderAmountTone,
    getOrderPackingLabels,
    isOrderUnitSingle,
    normalizeOrderUnitOfMeasure,
    resolveProductBoxSize
} from '../purchaserequisition/PurchaseRequisitionActualOrderUtils';
import DateInput from '../../shared/component/form/DateInput';
import Dialog from '../../shared/component/dialog/Dialog';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import '../../shared/styles/SettingsPage.css';

dayjs.extend(customParseFormat);

const requiredMark = <span style={{color: 'red'}}> (*)</span>;

const BARCODE_SEARCH_MODE = {
    BARCODE: 'BARCODE',
    BOX_BARCODE: 'BOX_BARCODE'
};

export default class PurchaseRequisitionItemMobileSave extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            item: {},
            supplierList: [],
            departmentList: [],
            taxList: [],
            costAlternativesExpanded: false,
            displaySupplierChangeDialog: false,
            supplierChangePage: 0,
            supplierChangeSearch: '',
            packingVisualExpanded: false,
            saving: false,
            barcodeSearchMode: BARCODE_SEARCH_MODE.BARCODE
        };
        this.purchaseRequisitionService = new PurchaseRequisitionService();
        this.supplierService = new SupplierService();
        this.productService = new ProductService();
        this.merchantBranchSettingsService = new MerchantBranchSettingsService();
        this.departmentService = new DepartmentService();
        this.taxService = new TaxService();
    }

    componentDidMount() {
        this.supplierService.findAll().then(response => {
            if (Array.isArray(response)) {
                this.setState({supplierList: response}, () => this.enrichMissingSupplierNames());
            }
        });
        this.departmentService.findAll().then(response => {
            if (Array.isArray(response)) {
                this.setState({departmentList: response});
            }
        });
        this.taxService.findAll().then(response => {
            if (Array.isArray(response)) {
                this.setState({taxList: response});
            }
        });
    }

    enrichMissingSupplierNames = () => {
        const item = this.state.item;
        if (!item) {
            return;
        }
        let next = item;
        let changed = false;
        if (!GeneralUtils.isNullOrEmpty(item.currentSupplierId) && GeneralUtils.isNullOrEmpty(item.currentSupplierName)) {
            const supplier = this.resolveSupplier(item.currentSupplierId);
            if (supplier?.name) {
                next = {...next, currentSupplierName: supplier.name};
                changed = true;
            }
        }
        if (!GeneralUtils.isNullOrEmpty(item.supplierId) && GeneralUtils.isNullOrEmpty(item.supplierName)) {
            const supplier = this.resolveSupplier(item.supplierId);
            if (supplier?.name) {
                next = {...next, supplierName: supplier.name};
                changed = true;
            }
        }
        if (changed) {
            this.setState({item: next});
        }
    };

    setData = (data) => {
        const item = Object.assign({}, data);
        const isDelivery = this.props.deliveryMode === true;
        const isReturn = this.props.returnMode === true;
        const usesBoxes = isDelivery || isReturn || (!isDelivery && !isReturn);
        if (usesBoxes) {
            if (item.boxSize == null || item.boxSize === '') {
                item.boxSize = 1;
            }
            if (item.deliveredBoxCount == null || item.deliveredBoxCount === '') {
                item.deliveredBoxCount = 1;
            }
        }
        if (!isDelivery && !isReturn) {
            item.orderUnitOfMeasure = normalizeOrderUnitOfMeasure(item.orderUnitOfMeasure);
        }
        if (isDelivery || isReturn) {
            item.orderUnitOfMeasure = normalizeOrderUnitOfMeasure(item.orderUnitOfMeasure);
        }
        if (isDelivery) {
            const docSupplierId = this.props.documentSupplierId ?? item.supplierId ?? item.currentSupplierId ?? null;
            const docSupplierName = this.props.documentSupplierName ?? item.supplierName ?? item.currentSupplierName ?? null;
            const docSupplierBssId = this.props.documentSupplierBssId ?? item.supplierBssId ?? null;
            Object.assign(item, this.applySupplierFields(item, docSupplierId, docSupplierName, docSupplierBssId));
        }
        if (!isDelivery && !isReturn
            && item.actualOrderAmount != null
            && (data.deliveredBoxCount == null || data.deliveredBoxCount === '')) {
            if (isOrderUnitSingle(item.orderUnitOfMeasure)) {
                item.deliveredBoxCount = item.actualOrderAmount;
            } else if (item.boxSize != null) {
                const derived = deriveOrderBoxCount(item.actualOrderAmount, item.boxSize);
                if (derived != null) {
                    item.deliveredBoxCount = derived;
                    const orderQty = calculateActualOrderAmount(derived, item.boxSize, item.orderUnitOfMeasure);
                    if (orderQty != null) {
                        item.actualOrderAmount = orderQty;
                        item.recommendedOrderAmount = orderQty;
                    }
                }
            }
        }
        if (!item.longName && item.itemName) {
            item.longName = item.itemName;
        }
        if (item.boxSizeFromProduct == null && !isDelivery && !isReturn) {
            item.boxSizeFromProduct = item.boxSizeLocked === true
                || (item.boxSize != null
                    && String(item.boxSize).trim() !== ''
                    && Number(item.boxSize) > 0);
        }
        if (item.boxSizeLocked == null) {
            item.boxSizeLocked = item.boxSizeFromProduct === true
                && item.boxSize != null
                && Number(item.boxSize) > 0;
        }
        // Existing product line: barcode locked until Clear (same as after search).
        if (item.barcodeIsSearched == null) {
            item.barcodeIsSearched = !GeneralUtils.isNullOrEmpty(item.itemBssId)
                || !GeneralUtils.isNullOrEmpty(item.barcode);
        }
        // 0 / null box → 1 editable; catalogue >0 stays locked.
        const resolvedBox = resolveProductBoxSize(item.boxSize);
        item.boxSize = resolvedBox.boxSize;
        if (item.boxSizeLocked == null) {
            item.boxSizeLocked = resolvedBox.boxSizeLocked;
        }
        if (item.boxSizeFromProduct == null) {
            item.boxSizeFromProduct = item.boxSizeLocked === true;
        }
        // Künye: restore catalogue cost marker after reopen.
        if (item.originalCurrentCost == null && item.currentCost != null) {
            item.originalCurrentCost = item.currentCost;
        }
        this.setState({
            item,
            costAlternativesExpanded: false,
            displaySupplierChangeDialog: false,
            supplierChangePage: 0,
            supplierChangeSearch: '',
            packingVisualExpanded: false,
            // Line stores UNIT barcode only; box barcode is lookup-only.
            barcodeSearchMode: BARCODE_SEARCH_MODE.BARCODE
        }, () => {
            if (!this.isReadOnly()) {
                this.focusBarcodeInput();
            }
            if (isDelivery || isReturn) {
                this.updateDeliveryCalcFields({});
            } else {
                this.updateOrderCalcFields({});
            }
        });
    };

    isReadOnly = () => this.props.readOnly === true;

    focusBarcodeInput = () => {
        if (this.isReadOnly()) {
            return;
        }
        setTimeout(() => {
            if (this.barcodeRef) {
                this.barcodeRef.focus();
            }
        }, 100);
    };

    resolveSupplier = (supplierId) => {
        if (GeneralUtils.isNullOrEmpty(supplierId)) {
            return null;
        }
        const normalizedId = Number(supplierId);
        const supplierList = Array.isArray(this.state.supplierList) ? this.state.supplierList : [];
        const supplier = supplierList.find(s =>
            Number(s.id) === normalizedId || s.id == supplierId
        );
        if (!supplier) {
            return null;
        }
        return {
            id: supplier.id,
            name: supplier.name,
            bssId: supplier.bssId || supplier.deposSupplierBssId || null
        };
    };

    applySupplierFields = (item, supplierId, supplierName, supplierBssId) => ({
        ...item,
        supplierId,
        supplierName,
        supplierBssId,
        currentSupplierId: supplierId,
        currentSupplierName: supplierName
    });

    /** Sets product's original supplier only (does not clear/set selected override). */
    applyCurrentSupplierFields = (item, supplierId, supplierName, supplierBssId = null) => ({
        ...item,
        currentSupplierId: supplierId,
        currentSupplierName: supplierName,
        // Default bss follows current until an override is chosen.
        supplierBssId: item.supplierId ? item.supplierBssId : supplierBssId
    });

    /** Sets selected/override supplier used for PO grouping & checkout. */
    applySelectedSupplierFields = (item, supplierId, supplierName, supplierBssId, unitCost = undefined) => {
        const next = {
            ...item,
            supplierId,
            supplierName,
            supplierBssId: supplierBssId ?? item.supplierBssId
        };
        // No catalogue supplier yet — treat the chosen supplier as current too (checkout requires an id).
        if (GeneralUtils.isNullOrEmpty(item.currentSupplierId)) {
            next.currentSupplierId = supplierId;
            next.currentSupplierName = supplierName;
        }
        if (unitCost !== undefined) {
            next.currentCost = unitCost;
        }
        return next;
    };

    clearSelectedSupplier = () => {
        if (this.isReadOnly()) {
            return;
        }
        this.setState(prev => ({
            item: {
                ...prev.item,
                supplierId: null,
                supplierName: null,
                supplierBssId: this.resolveSupplier(prev.item.currentSupplierId)?.bssId
                    ?? prev.item.supplierBssId,
                currentCost: prev.item.originalCurrentCost ?? prev.item.currentCost,
                cheaperCostAvailable: (prev.item.costAlternatives || []).length > 0
            },
            displaySupplierChangeDialog: false
        }));
    };

    applyLookupToItem = (lookup, options = {}) => {
        const isDelivery = this.props.deliveryMode === true;
        const isReturn = this.props.returnMode === true;
        const scannedBarcode = this.state.item?.barcode;
        let supplierId;
        let supplierName;
        let supplierBssId;
        if (isDelivery) {
            // DN supplier comes from document header — do not overwrite from product barcode.
            supplierId = this.props.documentSupplierId ?? this.state.item.supplierId ?? this.state.item.currentSupplierId ?? null;
            supplierName = this.props.documentSupplierName ?? this.state.item.supplierName ?? this.state.item.currentSupplierName ?? null;
            supplierBssId = this.props.documentSupplierBssId ?? this.state.item.supplierBssId ?? null;
        } else {
            const supplier = this.resolveSupplier(lookup.currentSupplierId || lookup.supplierId);
            supplierId = lookup.supplierId ?? lookup.currentSupplierId ?? supplier?.id ?? null;
            supplierName = lookup.supplierName ?? lookup.currentSupplierName ?? supplier?.name ?? null;
            supplierBssId = lookup.supplierBssId ?? supplier?.bssId ?? null;
        }
        let item = {
            ...this.state.item,
            // Keep scanned code if lookup has no unit barcode (never blank the field after search).
            barcode: !GeneralUtils.isNullOrEmpty(lookup.barcode)
                ? lookup.barcode
                : (scannedBarcode || ''),
            itemName: lookup.itemName,
            longName: lookup.longName || lookup.itemName,
            webImagePath: lookup.webImagePath || null,
            itemBssId: lookup.itemBssId,
            itemUnitMeasure: lookup.itemUnitMeasure,
            itemSize: lookup.itemSize,
            boxSize: lookup.boxSize,
            boxSizeFromProduct: false,
            boxSizeLocked: false,
            // Product panel always shows catalogue values (even on DN/Return).
            // Persist currentCost so künye survives save/reopen (DN form does not edit it).
            currentCost: lookup.currentCost,
            originalCurrentCost: lookup.currentCost,
            currentPrice: lookup.currentPrice ?? lookup.salePrice ?? lookup.price ?? null,
            minStock: lookup.minStock,
            maxStock: lookup.maxStock,
            currentStock: lookup.currentStock,
            departmentId: lookup.departmentId ?? this.state.item.departmentId ?? null,
            taxRate: lookup.taxRate ?? this.state.item.taxRate ?? null,
            profitMargin: lookup.profitMargin ?? this.state.item.profitMargin ?? null,
            barcodeIsSearched: true,
            damagedQuantity: isDelivery ? null : this.state.item.damagedQuantity,
            reasonCode: isReturn ? (this.state.item.reasonCode || '') : this.state.item.reasonCode,
            // New product lookup resets selected override (PR only).
            supplierId: isDelivery ? supplierId : null,
            supplierName: isDelivery ? supplierName : null
        };
        if (!isDelivery && !isReturn) {
            const baseQty = lookup.recommendedOrderAmount ?? lookup.actualOrderAmount;
            const resolvedBox = resolveProductBoxSize(lookup.boxSize);
            item.boxSize = resolvedBox.boxSize;
            item.boxSizeLocked = resolvedBox.boxSizeLocked;
            item.boxSizeFromProduct = resolvedBox.boxSizeLocked;
            item.orderUnitOfMeasure = normalizeOrderUnitOfMeasure(
                this.state.item.orderUnitOfMeasure || item.orderUnitOfMeasure
            );
            let orderCount;
            let orderQty;
            if (isOrderUnitSingle(item.orderUnitOfMeasure)) {
                orderCount = baseQty != null && String(baseQty).trim() !== '' ? baseQty : 1;
                orderQty = calculateActualOrderAmount(orderCount, item.boxSize, item.orderUnitOfMeasure);
            } else {
                orderCount = deriveOrderBoxCount(baseQty, item.boxSize) ?? 1;
                orderQty = calculateActualOrderAmount(orderCount, item.boxSize, item.orderUnitOfMeasure);
            }
            item.deliveredBoxCount = orderCount;
            item.actualOrderAmount = orderQty;
            // Recommended: total pieces for the chosen UOM.
            item.recommendedOrderAmount = orderQty != null ? orderQty : lookup.recommendedOrderAmount;
        } else if (isDelivery || isReturn) {
            const resolvedBox = resolveProductBoxSize(lookup.boxSize);
            item.boxSize = resolvedBox.boxSize;
            item.boxSizeLocked = resolvedBox.boxSizeLocked;
            item.boxSizeFromProduct = resolvedBox.boxSizeLocked;
            item.deliveredBoxCount = this.state.item.deliveredBoxCount != null && this.state.item.deliveredBoxCount !== ''
                ? this.state.item.deliveredBoxCount
                : 1;
            item.actualOrderAmount = isReturn ? this.state.item.actualOrderAmount : null;
            // Künye display: recommended from product lookup (not used as return/DN order qty).
            const baseQty = lookup.recommendedOrderAmount ?? lookup.actualOrderAmount;
            if (baseQty != null && String(baseQty).trim() !== '') {
                const boxes = deriveOrderBoxCount(baseQty, item.boxSize);
                const orderQty = boxes != null
                    ? calculateOrderAmountFromBoxes(boxes, item.boxSize)
                    : null;
                item.recommendedOrderAmount = orderQty != null ? orderQty : baseQty;
            } else {
                item.recommendedOrderAmount = null;
            }
            if (isDelivery) {
                item.supplierBssId = supplierBssId;
            }
        }
        if (isDelivery) {
            item = this.applySupplierFields(item, supplierId, supplierName, supplierBssId);
        } else {
            item = this.applyCurrentSupplierFields(item, supplierId, supplierName, supplierBssId);
            if (isReturn) {
                item = this.applySupplierFields(item, supplierId, supplierName, supplierBssId);
            }
        }
        this.setState({item, packingVisualExpanded: false}, () => {
            if (isDelivery || isReturn) {
                this.updateDeliveryCalcFields({});
            } else {
                this.updateOrderCalcFields({});
            }
            if (typeof options.afterApply === 'function') {
                options.afterApply();
            }
        });
    };

    getDefaultMinStock = () => {
        return this.merchantBranchSettingsService
            .findByBranchId(localStorage.getItem('merchantBranchId'))
            .then((response) => {
                if (!GeneralUtils.isNullOrEmpty(response) && GeneralUtils.isNullOrEmpty(response.type)) {
                    return response.minStock ?? null;
                }
                return null;
            });
    };

    getDefaultProfitMargin = () => {
        return this.merchantBranchSettingsService
            .findByBranchId(localStorage.getItem('merchantBranchId'))
            .then((response) => {
                if (!GeneralUtils.isNullOrEmpty(response) && GeneralUtils.isNullOrEmpty(response.type)) {
                    return response.profitMargin ?? 40;
                }
                return 40;
            });
    };

    getDefaultData = () => {
        this.merchantBranchSettingsService.findByBranchId(localStorage.getItem('merchantBranchId'))
            .then((response) => {
                if (GeneralUtils.isNullOrEmpty(response) || !GeneralUtils.isNullOrEmpty(response.type)) {
                    return;
                }
                this.setState(prev => {
                    const item = {...prev.item};
                    // Same defaults as invoice Product dialog after barcode not found
                    if (response.minStock != null) {
                        item.minStock = response.minStock;
                    } else {
                        item.minStock = null;
                    }
                    if (GeneralUtils.isNullOrEmpty(item.departmentId) && response.departmentId != null) {
                        item.departmentId = response.departmentId;
                    }
                    if (response.profitMargin != null) {
                        item.profitMargin = response.profitMargin;
                    } else if (item.profitMargin == null) {
                        item.profitMargin = 40;
                    }
                    // Settings maxStock if configured; otherwise invoice-style total units
                    if (response.maxStock != null && Number(response.maxStock) !== 0) {
                        item.maxStock = response.maxStock;
                        item.maxStockFromSettings = true;
                    } else {
                        item.maxStock = this.calcDefaultMaxStock(item);
                        item.maxStockFromSettings = false;
                    }
                    if (item.taxRate == null && item.departmentId != null) {
                        const dept = (prev.departmentList || []).find(d => Number(d.id) === Number(item.departmentId));
                        if (dept?.taxRate != null) {
                            item.taxRate = dept.taxRate;
                        }
                        if (dept?.profitMargin != null
                            && dept.profitMargin !== 0
                            && dept.profitMargin !== 40) {
                            item.profitMargin = dept.profitMargin;
                        }
                    }
                    // Invoice defaults Item Unit to each for new product definition
                    if (GeneralUtils.isNullOrEmpty(item.itemUnitMeasure)) {
                        item.itemUnitMeasure = 'each';
                    }
                    return {item};
                });
            });
    };

    /** Invoice: maxStock = deliveryQty * boxSize; PR uses boxes × box quantity. */
    calcDefaultMaxStock = (item) => {
        const boxes = Number(item?.deliveredBoxCount);
        const boxSize = Number(item?.boxSize);
        if (!Number.isNaN(boxes) && !Number.isNaN(boxSize)
            && item?.deliveredBoxCount !== '' && item?.deliveredBoxCount != null
            && item?.boxSize !== '' && item?.boxSize != null) {
            return boxes * boxSize;
        }
        const orderQty = Number(item?.actualOrderAmount);
        if (!Number.isNaN(orderQty) && item?.actualOrderAmount !== '' && item?.actualOrderAmount != null) {
            return orderQty;
        }
        return 0;
    };

    findByBarcode = () => {
        if (this.isReadOnly()) {
            return;
        }
        const barcode = this.state.item?.barcode;
        if (GeneralUtils.isNullOrEmpty(barcode)) {
            return;
        }
        const searchMode = this.state.barcodeSearchMode === BARCODE_SEARCH_MODE.BOX_BARCODE
            ? BARCODE_SEARCH_MODE.BOX_BARCODE
            : BARCODE_SEARCH_MODE.BARCODE;
        const searchPromise = searchMode === BARCODE_SEARCH_MODE.BOX_BARCODE
            ? this.purchaseRequisitionService.findProductByBoxBarcode(barcode)
            : this.purchaseRequisitionService.findProductByBarcode(barcode);
        const notFoundMessage = searchMode === BARCODE_SEARCH_MODE.BOX_BARCODE
            ? 'Box barcode not found. Enter product details and Save to create the product.'
            : 'Barcode not found. Enter product details and Save to create the product.';

        searchPromise.then(async (response) => {
            if (!GeneralUtils.isNullOrEmpty(response) && GeneralUtils.isNullOrEmpty(response.type)) {
                // After any successful lookup the field shows product UNIT barcode.
                // Reset mode so the combo matches what is stored (not the box code typed).
                if (searchMode === BARCODE_SEARCH_MODE.BOX_BARCODE) {
                    this.setState({barcodeSearchMode: BARCODE_SEARCH_MODE.BARCODE});
                }
                const notOnCatalogue = GeneralUtils.isNullOrEmpty(response.itemBssId)
                    || response.productExists === false
                    || response.productExists === 'false';
                this.applyLookupToItem(response, {
                    afterApply: notOnCatalogue
                        ? async () => {
                            const profitMargin = response.profitMargin
                                ?? await this.getDefaultProfitMargin();
                            this.setState(prev => ({
                                item: {
                                    ...prev.item,
                                    departmentId: response.departmentId ?? prev.item.departmentId,
                                    taxRate: response.taxRate ?? prev.item.taxRate,
                                    profitMargin,
                                    minStock: response.minStock ?? prev.item.minStock,
                                    maxStock: response.maxStock ?? prev.item.maxStock,
                                    barcodeIsSearched: true,
                                    barcode: prev.item.barcode || this.state.item?.barcode || ''
                                }
                            }), () => this.getDefaultData());
                        }
                        : null
                });
            } else {
                // Not found: clear product fields, keep barcode; apply branch settings like invoice Product.
                const profitMargin = await this.getDefaultProfitMargin();
                this.setState(prev => ({
                    item: {
                        ...prev.item,
                        barcode: prev.item.barcode,
                        itemBssId: null,
                        itemName: '',
                        longName: '',
                        webImagePath: null,
                        itemSize: 1,
                        itemUnitMeasure: 'each',
                        boxSize: 1,
                        deliveredBoxCount: 1,
                        orderUnitOfMeasure: normalizeOrderUnitOfMeasure(prev.item.orderUnitOfMeasure),
                        // Match packaging defaults for CASE (1×1); SINGLE uses order qty only.
                        actualOrderAmount: calculateActualOrderAmount(
                            1, 1, normalizeOrderUnitOfMeasure(prev.item.orderUnitOfMeasure)
                        ),
                        recommendedOrderAmount: null,
                        currentCost: null,
                        originalCurrentCost: null,
                        currentPrice: null,
                        currentStock: null,
                        minStock: null,
                        maxStock: null,
                        departmentId: null,
                        taxRate: null,
                        taxId: null,
                        salePrice: null,
                        profitMargin,
                        currentSupplierId: null,
                        currentSupplierName: null,
                        supplierId: null,
                        supplierName: null,
                        supplierBssId: null,
                        costAlternatives: [],
                        cheaperCostAvailable: false,
                        boxSizeFromProduct: false,
                        barcodeIsSearched: true,
                        maxStockFromSettings: false
                    }
                }), () => {
                    this.getDefaultData();
                    toast.info(notFoundMessage);
                });
            }
        });
    };

    onBarcodeSearchModeChange = (e) => {
        const value = e.target.parsedValue !== undefined ? e.target.parsedValue : e.target.value;
        this.setState({
            barcodeSearchMode: value === BARCODE_SEARCH_MODE.BOX_BARCODE
                ? BARCODE_SEARCH_MODE.BOX_BARCODE
                : BARCODE_SEARCH_MODE.BARCODE
        });
    };

    saveProduct = async () => {
        const item = this.state.item || {};
        const name = item.longName || item.itemName;
        if (GeneralUtils.isNullOrEmpty(name)) {
            toast.warn('Product Name is required');
            throw new Error('Product Name is required');
        }
        if (GeneralUtils.isNullOrEmpty(item.barcode)) {
            toast.warn('Barcode is required');
            throw new Error('Barcode is required');
        }
        if (GeneralUtils.isNullOrEmpty(item.departmentId)) {
            toast.warn('Department is required. Select a department or set default department in Settings.');
            throw new Error('Department is required');
        }
        if (item.itemSize == null || String(item.itemSize).trim() === '') {
            toast.warn('Item Size is required');
            throw new Error('Item Size is required');
        }
        const itemSizeNumber = Number(item.itemSize);
        if (Number.isNaN(itemSizeNumber) || itemSizeNumber <= 0) {
            toast.warn('Item Size must be greater than zero');
            throw new Error('Item Size must be greater than zero');
        }
        if (GeneralUtils.isNullOrEmpty(item.itemUnitMeasure)) {
            toast.warn('Unit is required');
            throw new Error('Unit is required');
        }
        let taxId = item.taxId;
        if (GeneralUtils.isNullOrEmpty(taxId) && item.taxRate != null) {
            const byRate = (this.state.taxList || []).find(t => Number(t.rate) === Number(item.taxRate));
            taxId = byRate?.id ?? null;
        }
        if (GeneralUtils.isNullOrEmpty(taxId) && item.departmentId != null) {
            const dept = (this.state.departmentList || []).find(d => Number(d.id) === Number(item.departmentId));
            if (dept?.taxRate != null) {
                const byDept = (this.state.taxList || []).find(t => Number(t.rate) === Number(dept.taxRate));
                taxId = byDept?.id ?? null;
            }
        }
        if (GeneralUtils.isNullOrEmpty(taxId)) {
            toast.warn('Tax is required');
            throw new Error('Tax is required');
        }
        if (item.salePrice == null || String(item.salePrice).trim() === '') {
            toast.warn('Sale Price is required');
            throw new Error('Sale Price is required');
        }
        const salePriceNumber = Number(item.salePrice);
        if (Number.isNaN(salePriceNumber) || salePriceNumber < 0) {
            toast.warn('Sale Price must be a valid number');
            throw new Error('Sale Price must be a valid number');
        }
        const profitMargin = item.profitMargin ?? await this.getDefaultProfitMargin();
        const data = {
            name,
            barcode: item.barcode,
            tax: taxId,
            weighable: item.weighable ?? false,
            profitMargin,
            quantity: itemSizeNumber,
            price: salePriceNumber,
            unit: item.itemUnitMeasure,
            minStock: item.minStock,
            maxStock: item.maxStock,
            departmentId: item.departmentId,
            productType: 'PRODUCT_LINE',
            status: 'ACTIVE',
            boxQuantity: item.boxSize != null && item.boxSize !== '' ? item.boxSize : 1,
            supplierId: item.currentSupplierId ?? item.supplierId ?? null
        };
        const response = await this.productService.createHiddenSuccess(data);
        if (GeneralUtils.isNullOrEmpty(response) || !GeneralUtils.isNullOrEmpty(response.type)) {
            const message = response?.type || 'Failed to create product';
            toast.error(message);
            throw new Error(message);
        }
        await new Promise(resolve => {
            this.setState(prev => ({
                item: {
                    ...prev.item,
                    itemBssId: response.bssId,
                    itemName: response.name || name,
                    longName: response.longname || response.name || name,
                    currentCost: response.costPrice ?? prev.item.currentCost,
                    originalCurrentCost: response.costPrice ?? prev.item.originalCurrentCost
                }
            }), resolve);
        });
        return response;
    };

    __handleChange = (e) => {
        if (this.isReadOnly()) {
            return;
        }
        const value = e.target.parsedValue !== undefined ? e.target.parsedValue : e.target.value;
        const name = e.target.name;
        let parsedValue = value;
        if (name === 'barcode' && value != null) {
            parsedValue = value.toString().replace(/[^0-9]/g, '');
        }
        this.setState(prev => {
            // Barcode edited: clear product/order fields immediately (search not required).
            if (name === 'barcode'
                && !this.props.deliveryMode
                && !this.props.returnMode
                && String(parsedValue || '') !== String(prev.item?.barcode || '')) {
                return {
                    item: {
                        ...prev.item,
                        barcode: parsedValue,
                        itemBssId: null,
                        itemName: '',
                        longName: '',
                        webImagePath: null,
                        itemSize: 1,
                        itemUnitMeasure: 'each',
                        boxSize: 1,
                        deliveredBoxCount: 1,
                        actualOrderAmount: calculateActualOrderAmount(
                            1, 1, normalizeOrderUnitOfMeasure(prev.item.orderUnitOfMeasure)
                        ),
                        recommendedOrderAmount: null,
                        currentCost: null,
                        originalCurrentCost: null,
                        currentPrice: null,
                        currentStock: null,
                        minStock: null,
                        maxStock: null,
                        departmentId: null,
                        taxRate: null,
                        taxId: null,
                        salePrice: null,
                        currentSupplierId: null,
                        currentSupplierName: null,
                        supplierId: null,
                        supplierName: null,
                        supplierBssId: null,
                        costAlternatives: [],
                        cheaperCostAvailable: false,
                        boxSizeFromProduct: false,
                        barcodeIsSearched: false,
                        maxStockFromSettings: false
                    }
                };
            }
            const next = {...prev.item, [name]: parsedValue};
            if (name === 'itemName') {
                next.longName = parsedValue;
                next.itemName = parsedValue;
            }
            if (name === 'departmentId') {
                const dept = (prev.departmentList || []).find(d => Number(d.id) === Number(parsedValue));
                if (dept?.taxRate != null) {
                    next.taxRate = dept.taxRate;
                    const taxMatch = (prev.taxList || []).find(t => Number(t.rate) === Number(dept.taxRate));
                    if (taxMatch?.id != null) {
                        next.taxId = taxMatch.id;
                    }
                }
                if (dept?.profitMargin != null) {
                    next.profitMargin = dept.profitMargin;
                }
            }
            if (name === 'taxId') {
                const taxMatch = (prev.taxList || []).find(t => Number(t.id) === Number(parsedValue));
                if (taxMatch?.rate != null) {
                    next.taxRate = taxMatch.rate;
                } else if (parsedValue === '' || parsedValue == null) {
                    next.taxRate = null;
                }
            }
            if (name === 'orderUnitOfMeasure') {
                next.orderUnitOfMeasure = normalizeOrderUnitOfMeasure(parsedValue);
                const calculated = calculateActualOrderAmount(
                    next.deliveredBoxCount, next.boxSize, next.orderUnitOfMeasure
                );
                if (calculated != null) {
                    next.actualOrderAmount = calculated;
                }
            }
            return {item: next};
        });
    };

    __handleSupplierChange = (e) => {
        if (this.isReadOnly()) {
            return;
        }
        const rawValue = e.target.parsedValue !== undefined ? e.target.parsedValue : e.target.value;
        const supplierId = GeneralUtils.isNullOrEmpty(rawValue) ? null : Number(rawValue);
        const supplier = this.resolveSupplier(supplierId);
        this.setState(prev => ({
            item: this.applySupplierFields(
                prev.item,
                supplierId,
                supplier?.name ?? null,
                supplier?.bssId ?? null
            )
        }));
    };

    _handleEnterKeyDown = (e) => {
        if (e.target.name === 'barcode') {
            const allowedKeys = ['Backspace', 'Tab', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Enter'];
            const isDigit = e.key.length === 1 && /[0-9]/.test(e.key);
            if (!isDigit && !allowedKeys.includes(e.key) && !e.ctrlKey && !e.metaKey) {
                e.preventDefault();
                return;
            }
        }
        if (e.key === 'Enter' || e.keyCode === 13) {
            e.preventDefault();
            this.findByBarcode();
            if (this.barcodeRef) {
                this.barcodeRef.focus();
            }
        }
    };

    _handleEnterInput = (e) => {
        if (e.nativeEvent.inputType === 'insertLineBreak') {
            e.preventDefault();
            this.findByBarcode();
            if (this.barcodeRef) {
                this.barcodeRef.focus();
            }
        }
    };

    validateItem = (item) => {
        if (GeneralUtils.isNullOrEmpty(item.barcode)) {
            toast.warn('Barcode is required');
            return false;
        }
        if (this.props.returnMode) {
            item.orderUnitOfMeasure = normalizeOrderUnitOfMeasure(item.orderUnitOfMeasure);
            const qtyLabel = isOrderUnitSingle(item.orderUnitOfMeasure) ? 'Pieces' : 'Boxes';
            const itemQty = item.deliveredBoxCount;
            const boxQuantity = item.boxSize;
            if (itemQty == null || String(itemQty).trim() === '') {
                toast.warn(`${qtyLabel} is required`);
                return false;
            }
            const itemQtyNumber = Number(itemQty);
            if (Number.isNaN(itemQtyNumber) || itemQtyNumber <= 0) {
                toast.warn(`${qtyLabel} must be greater than zero`);
                return false;
            }
            if (!isOrderUnitSingle(item.orderUnitOfMeasure)) {
                if (boxQuantity == null || String(boxQuantity).trim() === '') {
                    toast.warn('Box Qty is required');
                    return false;
                }
                const boxQuantityNumber = Number(boxQuantity);
                if (Number.isNaN(boxQuantityNumber) || boxQuantityNumber <= 0) {
                    toast.warn('Box Qty must be greater than zero');
                    return false;
                }
            }
            const returnQty = calculateActualOrderAmount(
                item.deliveredBoxCount, item.boxSize, item.orderUnitOfMeasure
            );
            if (returnQty == null) {
                toast.warn('Return Quantity is required');
                return false;
            }
            item.actualOrderAmount = returnQty;
            if (GeneralUtils.isNullOrEmpty(item.reasonCode)) {
                toast.warn('Reason Code is required');
                return false;
            }
            if (item.reasonCode === 'EXPIRED' && GeneralUtils.isNullOrEmpty(item.expiryDate)) {
                toast.warn('Expiry Date is required for Expired return items');
                return false;
            }
            if (item.reasonCode === 'OTHER' && GeneralUtils.isNullOrEmpty(item.description)) {
                toast.warn('Return Description is required when Reason is Other');
                return false;
            }
            const supplierId = item.supplierId ?? item.currentSupplierId;
            if (GeneralUtils.isNullOrEmpty(supplierId)) {
                toast.warn('Supplier is required');
                return false;
            }
            return true;
        }
        if (this.props.deliveryMode) {
            item.orderUnitOfMeasure = normalizeOrderUnitOfMeasure(item.orderUnitOfMeasure);
            const qtyLabel = isOrderUnitSingle(item.orderUnitOfMeasure) ? 'Pieces' : 'Boxes';
            const itemQty = item.deliveredBoxCount;
            const boxQuantity = item.boxSize;
            if (itemQty == null || String(itemQty).trim() === '') {
                toast.warn(`${qtyLabel} is required`);
                return false;
            }
            const itemQtyNumber = Number(itemQty);
            if (Number.isNaN(itemQtyNumber) || itemQtyNumber <= 0) {
                toast.warn(`${qtyLabel} must be greater than zero`);
                return false;
            }
            if (!isOrderUnitSingle(item.orderUnitOfMeasure)) {
                if (boxQuantity == null || String(boxQuantity).trim() === '') {
                    toast.warn('Box Qty is required');
                    return false;
                }
                const boxQuantityNumber = Number(boxQuantity);
                if (Number.isNaN(boxQuantityNumber) || boxQuantityNumber <= 0) {
                    toast.warn('Box Qty must be greater than zero');
                    return false;
                }
            }
            const deliveryQty = calculateActualOrderAmount(
                item.deliveredBoxCount, item.boxSize, item.orderUnitOfMeasure
            );
            if (deliveryQty == null) {
                toast.warn('Total Qty is required');
                return false;
            }
            item.actualOrderAmount = deliveryQty;
            const supplierId = item.supplierId ?? item.currentSupplierId;
            if (GeneralUtils.isNullOrEmpty(supplierId)) {
                toast.warn('Supplier is required');
                return false;
            }
            if (item.damagedQuantity != null && String(item.damagedQuantity).trim() !== '') {
                const damagedNumber = Number(item.damagedQuantity);
                if (Number.isNaN(damagedNumber) || damagedNumber < 0) {
                    toast.warn('Rejected Qty must be a valid number');
                    return false;
                }
                if (damagedNumber > deliveryQty) {
                    toast.warn('Rejected Qty cannot be greater than Total Qty');
                    return false;
                }
                if (damagedNumber > 0) {
                    if (GeneralUtils.isNullOrEmpty(item.reasonCode)) {
                        toast.warn('Rejected Reason is required when Rejected Qty is entered');
                        return false;
                    }
                    if (item.reasonCode === 'EXPIRED' && GeneralUtils.isNullOrEmpty(item.expiryDate)) {
                        toast.warn('Expiry Date is required for Expired rejected items');
                        return false;
                    }
                    if (item.reasonCode === 'OTHER' && GeneralUtils.isNullOrEmpty(item.description)) {
                        toast.warn('Rejected Description is required when Reason is Other');
                        return false;
                    }
                }
            }
            return true;
        }
        // OPEN PR (web popup or mobile form)
        {
            const supplierId = item.supplierId ?? item.currentSupplierId;
            if (GeneralUtils.isNullOrEmpty(item.currentSupplierId) && GeneralUtils.isNullOrEmpty(supplierId)) {
                toast.warn('Supplier is required');
                return false;
            }
            if (GeneralUtils.isNullOrEmpty(item.itemName) && GeneralUtils.isNullOrEmpty(item.longName)) {
                toast.warn('Product Name is required');
                return false;
            }
            // New product after barcode search (not found / depos-only): Item Size + Department required
            const isCreatingProduct = GeneralUtils.isNullOrEmpty(item.itemBssId)
                && item.barcodeIsSearched === true;
            if (isCreatingProduct) {
                if (item.itemSize == null || String(item.itemSize).trim() === '') {
                    toast.warn('Item Size is required');
                    return false;
                }
                const itemSizeNumber = Number(item.itemSize);
                if (Number.isNaN(itemSizeNumber) || itemSizeNumber <= 0) {
                    toast.warn('Item Size must be greater than zero');
                    return false;
                }
                if (GeneralUtils.isNullOrEmpty(item.itemUnitMeasure)) {
                    toast.warn('Unit is required');
                    return false;
                }
                if (GeneralUtils.isNullOrEmpty(item.departmentId)) {
                    toast.warn('Department is required to create a new product');
                    return false;
                }
                if (GeneralUtils.isNullOrEmpty(item.taxId)
                    && (item.taxRate == null || String(item.taxRate).trim() === '')) {
                    toast.warn('Tax is required to create a new product');
                    return false;
                }
                if (item.salePrice == null || String(item.salePrice).trim() === '') {
                    toast.warn('Sale Price is required to create a new product');
                    return false;
                }
                const salePriceNumber = Number(item.salePrice);
                if (Number.isNaN(salePriceNumber) || salePriceNumber < 0) {
                    toast.warn('Sale Price must be a valid number');
                    return false;
                }
            }
            if (item.deliveredBoxCount == null || String(item.deliveredBoxCount).trim() === '') {
                const qtyLabel = isOrderUnitSingle(item.orderUnitOfMeasure) ? 'Pieces' : 'Cases';
                toast.warn(`${qtyLabel} is required`);
                return false;
            }
            item.orderUnitOfMeasure = normalizeOrderUnitOfMeasure(item.orderUnitOfMeasure);
            // Box Qty only multiplies for Case; Single uses Pieces alone.
            if (!isOrderUnitSingle(item.orderUnitOfMeasure)) {
                if (item.boxSize == null || String(item.boxSize).trim() === '') {
                    toast.warn('Box Qty is required');
                    return false;
                }
                const boxQtyNumber = Number(item.boxSize);
                if (Number.isNaN(boxQtyNumber) || boxQtyNumber <= 0) {
                    toast.warn('Box Qty must be greater than zero');
                    return false;
                }
            }
            const orderQtyNumber = Number(item.deliveredBoxCount);
            if (Number.isNaN(orderQtyNumber) || orderQtyNumber <= 0) {
                const qtyLabel = isOrderUnitSingle(item.orderUnitOfMeasure) ? 'Pieces' : 'Cases';
                toast.warn(`${qtyLabel} must be greater than zero`);
                return false;
            }
            const calculated = calculateActualOrderAmount(
                item.deliveredBoxCount, item.boxSize, item.orderUnitOfMeasure
            );
            if (calculated != null) {
                item.actualOrderAmount = calculated;
            }
        }
        const actualOrderAmount = item.actualOrderAmount;
        if (actualOrderAmount == null || String(actualOrderAmount).trim() === '') {
            toast.warn('Total Pieces is required');
            return false;
        }
        return true;
    };

    handleSave = async (closeAfter = true) => {
        if (this.state.saving) {
            return;
        }
        let item = normalizeItem({...this.state.item});
        if (!this.validateItem(item)) {
            return;
        }
        const needsProductCreate = !this.props.deliveryMode
            && !this.props.returnMode
            && GeneralUtils.isNullOrEmpty(item.itemBssId);
        if (needsProductCreate) {
            this.setState({saving: true});
            try {
                await this.saveProduct();
                item = normalizeItem({...this.state.item});
                if (!this.validateItem(item)) {
                    this.setState({saving: false});
                    return;
                }
            } catch (e) {
                this.setState({saving: false});
                return;
            }
            this.setState({saving: false});
        }
        if (closeAfter) {
            this.props.saveItem(item);
        } else {
            this.props.saveItemAndNext(item);
        }
    };

    applyAlternativeSupplier = (alternative) => {
        if (!alternative || this.isReadOnly()) {
            return;
        }
        this.selectOverrideSupplier({
            id: alternative.supplierId ?? null,
            name: alternative.supplierName ?? null,
            bssId: alternative.supplierBssId ?? null
        });
    };

    selectOverrideSupplier = (supplier) => {
        if (!supplier || this.isReadOnly()) {
            return;
        }
        const currentId = this.state.item?.currentSupplierId;
        if (currentId != null && Number(supplier.id) === Number(currentId)) {
            toast.warn('Please select a supplier different from the current supplier');
            return;
        }
        const nextCost = supplier.unitCost != null && supplier.unitCost !== ''
            ? supplier.unitCost
            : null;
        this.setState(prev => ({
            item: this.applySelectedSupplierFields(
                {
                    ...prev.item,
                    cheaperCostAvailable: false,
                    currentCost: nextCost
                },
                supplier.id ?? null,
                supplier.name ?? null,
                supplier.bssId ?? null,
                nextCost
            ),
            displaySupplierChangeDialog: false
        }));
    };

    openSupplierChangeDialog = () => {
        if (this.isReadOnly()) {
            return;
        }
        this.setState({
            displaySupplierChangeDialog: true,
            supplierChangePage: 0,
            supplierChangeSearch: ''
        });
    };

    closeSupplierChangeDialog = () => {
        this.setState({
            displaySupplierChangeDialog: false,
            supplierChangePage: 0,
            supplierChangeSearch: ''
        });
    };

    handleSupplierChangeSearch = (e) => {
        this.setState({
            supplierChangeSearch: e?.target?.value || '',
            supplierChangePage: 0
        });
    };

    getSupplierChangeOptions = (item) => {
        const currentId = item?.currentSupplierId != null ? Number(item.currentSupplierId) : null;
        const alternatives = (Array.isArray(item?.costAlternatives) ? item.costAlternatives : []).filter(alt =>
            alt?.supplierId == null || Number(alt.supplierId) !== currentId
        );
        const alternativeIds = new Set(
            alternatives
                .map(alt => alt.supplierId != null ? Number(alt.supplierId) : null)
                .filter(id => id != null)
        );
        const supplierList = Array.isArray(this.state.supplierList) ? this.state.supplierList : [];
        const otherSuppliers = supplierList
            .filter(s => currentId == null || Number(s.id) !== currentId)
            .filter(s => !alternativeIds.has(Number(s.id)))
            .map(s => ({
                supplierId: s.id,
                supplierName: s.name,
                supplierBssId: s.bssId || s.deposSupplierBssId || null,
                unitCost: null,
                isAlternative: false
            }));
        const cheapOptions = alternatives.map(alt => ({
            supplierId: alt.supplierId,
            supplierName: alt.supplierName,
            supplierBssId: alt.supplierBssId,
            unitCost: alt.unitCost,
            isAlternative: true
        }));
        return [...cheapOptions, ...otherSuppliers];
    };

    formatMoney = (value) => {
        if (value == null || value === '') {
            return '-';
        }
        const number = Number(value);
        if (Number.isNaN(number)) {
            return value;
        }
        return `£${number.toFixed(2)}`;
    };

    renderCheaperSupplierHint(item) {
        if (!this.props.webPopup || this.isReadOnly() || !item?.cheaperCostAvailable) {
            return null;
        }
        const count = (item.costAlternatives || []).length;
        if (count <= 0) {
            return null;
        }
        return (
            <span className="pr-cheaper-supplier-hint pr-cheaper-supplier-hint--inline" role="status">
                Cheaper option{count === 1 ? '' : 's'}
            </span>
        );
    }

    isFieldDisabled = (fieldDisabled = false) => this.isReadOnly() || fieldDisabled;

    renderNumberField = (name, label, fieldDisabled = false, required = false) => {
        const disabled = this.isFieldDisabled(fieldDisabled);
        const readonlyClass = disabled
            ? ' purchase-requisition-item-form__input--readonly'
            : ' purchase-requisition-item-form__input--editable';
        const rawValue = this.state.item[name];
        if (disabled && (rawValue == null || rawValue === '')) {
            return (
                <AvGroup>
                    <Label for={name}>
                        {label}
                        {required && requiredMark}
                    </Label>
                    <input
                        type="text"
                        className={`form-control${readonlyClass}`}
                        value="—"
                        disabled={true}
                        readOnly={true}
                    />
                </AvGroup>
            );
        }
        return (
            <AvGroup>
                <Label for={name}>
                    {label}
                    {required && requiredMark}
                </Label>
                <NumberFormat
                    value={rawValue}
                    className={`number-format-input form-control${readonlyClass}`}
                    thousandSeparator={true}
                    decimalScale={2}
                    fixedDecimalScale={false}
                    onValueChange={(values) => {
                        if (disabled) {
                            return;
                        }
                        this.setState(prev => ({
                            item: {...prev.item, [name]: values.value}
                        }));
                    }}
                    disabled={disabled}
                />
            </AvGroup>
        );
    };

    renderFooterButtons() {
        if (this.isReadOnly()) {
            return (
                <Button
                    type="button"
                    color="outline-primary"
                    className="settings-page__btn--action"
                    onClick={this.props.closeItemDialog}
                >
                    Close
                </Button>
            );
        }
        return (
            <>
                <Button
                    type="button"
                    color="outline-primary"
                    className="settings-page__btn--action"
                    onClick={() => this.handleSave(true)}
                >
                    Save
                </Button>
                <Button
                    type="button"
                    color="outline-primary"
                    className="settings-page__btn--action"
                    onClick={() => this.handleSave(false)}
                >
                    Save &amp; Next
                </Button>
                <Button
                    type="button"
                    color="outline-primary"
                    className="settings-page__btn--action"
                    onClick={this.props.closeItemDialog}
                >
                    Close
                </Button>
            </>
        );
    }

    renderFooter() {
        if (this.isReadOnly()) {
            if (this.props.webPopup) {
                return (
                    <div className="purchase-requisition-item-form__footer">
                        <div className="settings-page__toolbar settings-page__toolbar--end settings-page__toolbar--equal-height">
                            {this.renderFooterButtons()}
                        </div>
                    </div>
                );
            }
            return (
                <div className="mobile-fixed-footer">
                    <div className="mobile-fixed-footer-inner">
                        <Button
                            type="button"
                            color="outline-primary"
                            className="settings-page__btn--cancel"
                            style={{height: '30px', width: '110px'}}
                            onClick={this.props.closeItemDialog}
                        >
                            Close
                        </Button>
                    </div>
                </div>
            );
        }
        if (this.props.webPopup) {
            return (
                <div className="purchase-requisition-item-form__footer">
                    <div className="settings-page__toolbar settings-page__toolbar--end settings-page__toolbar--equal-height">
                        {this.renderFooterButtons()}
                    </div>
                </div>
            );
        }

        return (
            <div className="mobile-fixed-footer">
                <div className="mobile-fixed-footer-inner">
                    <Button
                        type="button"
                        color="outline-primary"
                        className="settings-page__btn--action"
                        style={{marginRight: '5px', height: '30px', width: '110px'}}
                        onClick={() => this.handleSave(true)}
                    >
                        Save
                    </Button>
                    <Button
                        type="button"
                        color="outline-primary"
                        className="settings-page__btn--action"
                        style={{marginRight: '5px', height: '30px', width: '130px'}}
                        onClick={() => this.handleSave(false)}
                    >
                        Save &amp; Next
                    </Button>
                    <Button
                        type="button"
                        color="outline-primary"
                        className="settings-page__btn--cancel"
                        style={{height: '30px', width: '110px'}}
                        onClick={this.props.closeItemDialog}
                    >
                        Close
                    </Button>
                </div>
            </div>
        );
    }

    isBoxSizeFieldDisabled = (item) => {
        // Mobile: Box Qty is always read-only (catalogue / default 1).
        if (this.props.webPopup !== true) {
            return true;
        }
        if (this.isReadOnly()) {
            return true;
        }
        // Product / line pack size fixed when catalogue value is > 0.
        return item?.boxSizeLocked === true
            || (item?.boxSizeFromProduct === true
                && item?.boxSize != null
                && Number(item.boxSize) > 0);
    };

    isBarcodeLocked = (item = this.state.item) => {
        if (this.isReadOnly()) {
            return true;
        }
        return item?.barcodeIsSearched === true
            || !GeneralUtils.isNullOrEmpty(item?.itemBssId)
            || this.hasExistingProduct(item);
    };

    clearBarcodeSearch = () => {
        if (this.isReadOnly()) {
            return;
        }
        this.setState(prev => ({
            item: {
                ...prev.item,
                barcode: '',
                barcodeIsSearched: false,
                itemBssId: null,
                itemName: '',
                longName: '',
                webImagePath: null,
                itemSize: 1,
                itemUnitMeasure: 'each',
                boxSize: 1,
                boxSizeLocked: false,
                boxSizeFromProduct: false,
                deliveredBoxCount: 1,
                actualOrderAmount: calculateActualOrderAmount(
                    1, 1, normalizeOrderUnitOfMeasure(prev.item.orderUnitOfMeasure)
                ),
                recommendedOrderAmount: null,
                currentCost: null,
                originalCurrentCost: null,
                currentPrice: null,
                currentStock: null,
                minStock: null,
                maxStock: null,
                departmentId: null,
                taxRate: null,
                taxId: null,
                salePrice: null,
                currentSupplierId: null,
                currentSupplierName: null,
                supplierId: null,
                supplierName: null,
                supplierBssId: null,
                costAlternatives: [],
                cheaperCostAvailable: false,
                maxStockFromSettings: false
            },
            barcodeSearchMode: prev.barcodeSearchMode || BARCODE_SEARCH_MODE.BARCODE,
            supplierChangePage: 0
        }), () => {
            if (this.barcodeRef && typeof this.barcodeRef.focus === 'function') {
                this.barcodeRef.focus();
            }
        });
    };

    deleteCurrentItem = () => {
        if (this.isReadOnly()) {
            return;
        }
        if (typeof this.props.deleteItem === 'function') {
            this.props.deleteItem(this.state.item);
            return;
        }
        if (typeof this.props.closeItemDialog === 'function') {
            this.props.closeItemDialog();
        }
    };

    renderBarcodeField(item) {
        const formReadOnly = this.isReadOnly();
        const barcodeLocked = this.isBarcodeLocked(item);
        const disabled = formReadOnly || barcodeLocked;
        const searchMode = this.state.barcodeSearchMode === BARCODE_SEARCH_MODE.BOX_BARCODE
            ? BARCODE_SEARCH_MODE.BOX_BARCODE
            : BARCODE_SEARCH_MODE.BARCODE;
        const placeholder = searchMode === BARCODE_SEARCH_MODE.BOX_BARCODE
            ? 'Scan box barcode'
            : 'Scan barcode';
        return (
            <AvGroup>
                <Label for="barcode">
                    Barcode
                    {!formReadOnly && requiredMark}
                </Label>
                <div className="settings-page__barcode-input-row">
                    {!formReadOnly && !barcodeLocked &&
                        <select
                            className="form-control settings-page__barcode-search-mode"
                            name="barcodeSearchMode"
                            value={searchMode}
                            onChange={this.onBarcodeSearchModeChange}
                            title={searchMode === BARCODE_SEARCH_MODE.BOX_BARCODE
                                ? 'Search by box barcode'
                                : 'Search by barcode'}
                        >
                            <option value={BARCODE_SEARCH_MODE.BARCODE}>Barcode</option>
                            <option value={BARCODE_SEARCH_MODE.BOX_BARCODE}>Box barcode</option>
                        </select>
                    }
                    <AvField
                        type="text"
                        name="barcode"
                        id="barcode"
                        innerRef={(el) => (this.barcodeRef = el)}
                        className={`form-control${disabled ? ' purchase-requisition-item-form__input--readonly' : ''}`}
                        value={item.barcode || ''}
                        onChange={this.__handleChange}
                        onKeyDown={this._handleEnterKeyDown}
                        onInput={this._handleEnterInput}
                        autoComplete="off"
                        enterKeyHint="done"
                        disabled={disabled}
                        placeholder={placeholder}
                    />
                    {!formReadOnly && !barcodeLocked &&
                        <Button
                            type="button"
                            color="outline-primary"
                            className="settings-page__btn--search"
                            onClick={this.findByBarcode}
                            title={searchMode === BARCODE_SEARCH_MODE.BOX_BARCODE
                                ? 'Search by box barcode'
                                : 'Search by barcode'}
                        >
                            <i className="pi pi-search" aria-hidden="true"/>
                        </Button>
                    }
                    {!formReadOnly && barcodeLocked && this.props.webPopup === true &&
                        <Button
                            type="button"
                            color="outline-primary"
                            className="settings-page__btn--search settings-page__btn--barcode-clear"
                            onClick={this.clearBarcodeSearch}
                            title="Clear barcode"
                            aria-label="Clear barcode"
                        >
                            <i className="pi pi-times" aria-hidden="true"/>
                        </Button>
                    }
                    {!formReadOnly && barcodeLocked && this.props.webPopup !== true &&
                        <Button
                            type="button"
                            color="outline-danger"
                            className="settings-page__btn--search settings-page__btn--barcode-delete"
                            onClick={this.deleteCurrentItem}
                            title="Delete item"
                            aria-label="Delete item"
                        >
                            Delete
                        </Button>
                    }
                </div>
            </AvGroup>
        );
    }

    isOpenRequisitionEdit = () => !this.isReadOnly()
        && !this.props.deliveryMode
        && !this.props.returnMode;

    isNewProduct = () => this.isOpenRequisitionEdit()
        && GeneralUtils.isNullOrEmpty(this.state.item?.itemBssId)
        && this.state.item?.barcodeIsSearched === true;

    /** Existing catalogue product linked to the line (bss id set). */
    hasExistingProduct = (item = this.state.item) => !GeneralUtils.isNullOrEmpty(item?.itemBssId);

    renderProductNameField(item, isWeb) {
        const disabled = this.isReadOnly()
            || this.hasExistingProduct(item)
            || (isWeb && (this.props.deliveryMode || this.props.returnMode));
        return (
            <AvGroup>
                <Label for="itemName">
                    <Translate contentKey="purchaseOrderItem.itemName">Product Name</Translate>
                    {isWeb && !disabled && requiredMark}
                </Label>
                <AvField
                    type="text"
                    name="itemName"
                    className={disabled ? 'purchase-requisition-item-form__input--readonly' : 'purchase-requisition-item-form__input--editable'}
                    value={item.longName || item.itemName || ''}
                    onChange={this.__handleChange}
                    disabled={disabled}
                />
            </AvGroup>
        );
    }

    shouldShowProductNameUnderBarcode = (item) => {
        return this.isNewProduct()
            || this.hasExistingProduct(item)
            || !GeneralUtils.isNullOrEmpty(item?.longName)
            || !GeneralUtils.isNullOrEmpty(item?.itemName);
    };

    renderItemSizeField(item) {
        const editable = this.isOpenRequisitionEdit();
        if (!editable) {
            return this.renderReadOnlyValue('Item Size', item.itemSize);
        }
        const requireSize = this.isNewProduct();
        const rawValue = item.itemSize;
        return (
            <AvGroup>
                <Label for="itemSize" className="purchase-requisition-item-form__label--nowrap">
                    Item Size{requireSize && requiredMark}
                </Label>
                <NumberFormat
                    value={rawValue}
                    className="number-format-input form-control purchase-requisition-item-form__input--editable"
                    thousandSeparator={true}
                    decimalScale={2}
                    fixedDecimalScale={false}
                    onValueChange={(values) => {
                        this.setState(prev => ({
                            item: {...prev.item, itemSize: values.value}
                        }));
                    }}
                    disabled={false}
                />
            </AvGroup>
        );
    }

    renderItemUnitField(item) {
        const editable = this.isOpenRequisitionEdit();
        if (!editable) {
            return this.renderReadOnlyValue('Item Unit', this.getUnitDisplayName(item.itemUnitMeasure));
        }
        const requireUnit = this.isNewProduct();
        return (
            <AvGroup>
                <Label for="itemUnitMeasure" className="purchase-requisition-item-form__label--nowrap">
                    Item Unit{requireUnit && requiredMark}
                </Label>
                <AvField
                    type="select"
                    name="itemUnitMeasure"
                    className="purchase-requisition-item-form__input--editable"
                    value={item.itemUnitMeasure || ''}
                    onChange={this.__handleChange}
                    disabled={false}
                >
                    <option value="" key=""/>
                    {(PRODUCT_UNIT || []).map(element => (
                        <option value={element.value} key={element.value}>{element.name}</option>
                    ))}
                </AvField>
            </AvGroup>
        );
    }

    renderDepartmentField(item) {
        if (!this.isNewProduct()) {
            return null;
        }
        return (
            <AvGroup>
                <Label for="departmentId">
                    Department
                    {requiredMark}
                </Label>
                <AvField
                    type="select"
                    name="departmentId"
                    className="purchase-requisition-item-form__input--editable"
                    value={item.departmentId || ''}
                    onChange={this.__handleChange}
                >
                    <option value="" key=""/>
                    {(this.state.departmentList || []).map(element => (
                        <option value={element.id} key={element.id}>{element.name}</option>
                    ))}
                </AvField>
            </AvGroup>
        );
    }

    renderTaxField(item) {
        if (!this.isNewProduct()) {
            return null;
        }
        return (
            <AvGroup>
                <Label for="taxId">
                    Tax
                    {requiredMark}
                </Label>
                <AvField
                    type="select"
                    name="taxId"
                    className="purchase-requisition-item-form__input--editable"
                    value={item.taxId || ''}
                    onChange={this.__handleChange}
                >
                    <option value="" key=""/>
                    {(this.state.taxList || []).map(element => (
                        <option value={element.id} key={element.id}>
                            {element.name || (element.rate != null ? `${element.rate}%` : element.id)}
                        </option>
                    ))}
                </AvField>
            </AvGroup>
        );
    }

    renderSalePriceField(item) {
        if (!this.isNewProduct()) {
            return null;
        }
        return (
            <AvGroup>
                <Label for="salePrice" className="purchase-requisition-item-form__label--nowrap">
                    Sale Price
                    {requiredMark}
                </Label>
                <NumberFormat
                    value={item.salePrice}
                    className="number-format-input form-control purchase-requisition-item-form__input--editable"
                    thousandSeparator={true}
                    decimalScale={2}
                    fixedDecimalScale={true}
                    prefix={'£'}
                    onValueChange={(values) => {
                        this.setState(prev => ({
                            item: {...prev.item, salePrice: values.value}
                        }));
                    }}
                    disabled={false}
                />
            </AvGroup>
        );
    }

    renderSupplierField(item, isWeb) {
        // Delivery note supplier is fixed from document header.
        if (this.props.deliveryMode) {
            return (
                <AvGroup>
                    <Label for="currentSupplierId">Supplier</Label>
                    <AvField
                        type="text"
                        name="supplierName"
                        className="purchase-requisition-item-form__input--readonly"
                        value={item.supplierName || item.currentSupplierName || this.props.documentSupplierName || ''}
                        disabled={true}
                    />
                </AvGroup>
            );
        }
        // Return Note: same Current Supplier + Change / New Supplier pattern as PR (web + mobile).
        if (this.props.returnMode) {
            return this.renderWebSupplierFields(item);
        }
        // Mobile PR item form uses compact supplier select.
        if (!isWeb) {
            const disabled = this.isReadOnly();
            return (
                <AvGroup>
                    <Label for="currentSupplierId">
                        Supplier
                        {!disabled && requiredMark}
                    </Label>
                    <AvField
                        type="select"
                        name="supplierId"
                        className={disabled ? 'purchase-requisition-item-form__input--readonly' : ''}
                        value={item.supplierId || item.currentSupplierId || ''}
                        onChange={this.__handleSupplierChange}
                        disabled={disabled}
                    >
                        <option value="" key=""/>
                        {(Array.isArray(this.state.supplierList) ? this.state.supplierList : []).map(element => (
                            <option value={element.id} key={element.id}>{element.name}</option>
                        ))}
                    </AvField>
                </AvGroup>
            );
        }
        return this.renderWebSupplierFields(item);
    }

    renderWebSupplierFields(item) {
        const disabled = this.isReadOnly();
        const override = hasSelectedSupplierOverride(item);
        const cheaperHint = this.renderCheaperSupplierHint(item);
        return (
            <>
                <div className="purchase-requisition-item-form__row purchase-requisition-item-form__row--supplier">
                    <div className={`purchase-requisition-item-form__cell purchase-requisition-item-form__cell--${override ? '6' : '12'}`}>
                        <AvGroup className="pr-supplier-field">
                            <Label for="currentSupplierName">
                                Supplier
                                {!disabled && requiredMark}
                            </Label>
                            <div className="pr-supplier-current-row">
                                <input
                                    type="text"
                                    className="form-control purchase-requisition-item-form__input--readonly"
                                    value={item.currentSupplierName || ''}
                                    disabled={true}
                                    readOnly={true}
                                />
                                {!disabled &&
                                    <Button
                                        color="outline-primary"
                                        className="settings-page__btn--action pr-supplier-change-btn"
                                        onClick={this.openSupplierChangeDialog}
                                    >
                                        Change
                                    </Button>
                                }
                            </div>
                            {cheaperHint}
                        </AvGroup>
                    </div>
                    {override &&
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--6">
                            <AvGroup className="pr-supplier-field">
                                <Label for="selectedSupplierName">
                                    New Supplier
                                    <span className="pr-supplier-changed-badge">Changed</span>
                                </Label>
                                <div className="pr-supplier-selected-row">
                                    <div className="pr-supplier-selected-input-wrap">
                                        <input
                                            type="text"
                                            className="form-control purchase-requisition-item-form__input--readonly pr-supplier-selected--changed"
                                            value={item.supplierName || ''}
                                            disabled={true}
                                            readOnly={true}
                                        />
                                        {!disabled &&
                                            <button
                                                type="button"
                                                className="pr-supplier-clear-icon"
                                                onClick={this.clearSelectedSupplier}
                                                title="Clear"
                                                aria-label="Clear new supplier"
                                            >
                                                <i className="pi pi-times" aria-hidden="true"/>
                                            </button>
                                        }
                                    </div>
                                </div>
                            </AvGroup>
                        </div>
                    }
                </div>
                {this.renderSupplierChangeDialog(item)}
            </>
        );
    }

    renderSupplierChangeDialog(item) {
        const options = this.getSupplierChangeOptions(item);
        const search = (this.state.supplierChangeSearch || '').trim().toLowerCase();
        const filtered = search
            ? options.filter(option =>
                String(option.supplierName || '').toLowerCase().includes(search)
            )
            : options;
        const pageSize = 8;
        const total = filtered.length;
        const pageCount = Math.max(1, Math.ceil(total / pageSize));
        const page = Math.min(Math.max(0, this.state.supplierChangePage || 0), pageCount - 1);
        const pageRows = filtered.slice(page * pageSize, page * pageSize + pageSize);
        return (
            <Dialog
                visible={this.state.displaySupplierChangeDialog === true}
                header="Change Supplier"
                onHide={this.closeSupplierChangeDialog}
                style={{width: '560px', maxWidth: '95vw'}}
            >
                <div className="pr-supplier-change-dialog">
                    <p className="pr-supplier-change-dialog__hint">
                        Select a supplier different from the current supplier
                        {item.currentSupplierName ? ` (${item.currentSupplierName})` : ''}.
                    </p>
                    {options.length === 0
                        ? <div className="text-muted">No other suppliers available.</div>
                        : (
                            <>
                                <div className="pr-supplier-change-dialog__search">
                                    <i className="pi pi-search" aria-hidden="true"/>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search supplier..."
                                        value={this.state.supplierChangeSearch || ''}
                                        onChange={this.handleSupplierChangeSearch}
                                        autoComplete="off"
                                    />
                                </div>
                                {total === 0
                                    ? <div className="text-muted">No suppliers match your search.</div>
                                    : (
                                        <>
                                            <div className="pr-supplier-change-dialog__table-wrap">
                                                <table className="pr-supplier-change-dialog__table">
                                                    <thead>
                                                        <tr>
                                                            <th>Supplier</th>
                                                            <th className="pr-supplier-change-dialog__cost-col">Unit Cost</th>
                                                            <th className="pr-supplier-change-dialog__action-col"/>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {pageRows.map((option, index) => (
                                                            <tr key={`${option.supplierId || option.supplierName || index}`}>
                                                                <td>{option.supplierName || ''}</td>
                                                                <td className="pr-supplier-change-dialog__cost-col">
                                                                    {option.unitCost != null ? this.formatMoney(option.unitCost) : ''}
                                                                </td>
                                                                <td className="pr-supplier-change-dialog__action-col">
                                                                    <Button
                                                                        color="outline-primary"
                                                                        size="sm"
                                                                        className="settings-page__btn--action"
                                                                        onClick={() => this.selectOverrideSupplier({
                                                                            id: option.supplierId,
                                                                            name: option.supplierName,
                                                                            bssId: option.supplierBssId,
                                                                            unitCost: option.unitCost
                                                                        })}
                                                                    >
                                                                        Select
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {total > pageSize &&
                                                <div className="pr-supplier-change-dialog__pager">
                                                    <Button
                                                        color="outline-primary"
                                                        size="sm"
                                                        type="button"
                                                        className="pr-supplier-change-dialog__pager-btn"
                                                        disabled={page <= 0}
                                                        title="Previous"
                                                        aria-label="Previous page"
                                                        onClick={() => this.setState({supplierChangePage: page - 1})}
                                                    >
                                                        <i className="pi pi-chevron-left" aria-hidden="true"/>
                                                    </Button>
                                                    <span className="pr-supplier-change-dialog__pager-label">
                                                        {page + 1} / {pageCount}
                                                    </span>
                                                    <Button
                                                        color="outline-primary"
                                                        size="sm"
                                                        type="button"
                                                        className="pr-supplier-change-dialog__pager-btn"
                                                        disabled={page >= pageCount - 1}
                                                        title="Next"
                                                        aria-label="Next page"
                                                        onClick={() => this.setState({supplierChangePage: page + 1})}
                                                    >
                                                        <i className="pi pi-chevron-right" aria-hidden="true"/>
                                                    </Button>
                                                </div>
                                            }
                                        </>
                                    )
                                }
                            </>
                        )
                    }
                    <div className="settings-page__actions settings-page__actions--dialog-footer" style={{justifyContent: 'flex-start'}}>
                        <Button
                            color="outline-primary"
                            className="settings-page__btn--cancel"
                            onClick={this.closeSupplierChangeDialog}
                        >
                            Close
                        </Button>
                    </div>
                </div>
            </Dialog>
        );
    }

    renderCurrentCostField(item) {
        // Purchase requisition: current unit cost is always read-only (catalogue cost).
        const empty = item.currentCost == null || item.currentCost === '';
        return (
            <AvGroup>
                <Label for="currentCost">Cur. Unit Cost</Label>
                <input
                    id="currentCost"
                    type="text"
                    className="form-control purchase-requisition-item-form__input--readonly"
                    value={empty ? '—' : this.formatMoney(item.currentCost)}
                    disabled={true}
                    readOnly={true}
                />
            </AvGroup>
        );
    }

    renderKunyeValue = (label, value, options = {}) => {
        let display = value;
        if (display == null || display === '' || display === 'null' || display === 'undefined') {
            display = '—';
        } else if (options.isCurrency) {
            const num = Number(display);
            display = !Number.isNaN(num) ? this.formatMoney(num) : '—';
        }
        return (
            <div className="purchase-requisition-item-kunye__row">
                <div className="purchase-requisition-item-kunye__label">{label}</div>
                <div className="purchase-requisition-item-kunye__value">{display}</div>
            </div>
        );
    };

    /** Editable stock fields live only in CURRENT (not duplicated on the left form). */
    renderKunyeEditableNumber = (name, label, value) => (
        <div className="purchase-requisition-item-kunye__row purchase-requisition-item-kunye__row--field">
            <div className="purchase-requisition-item-kunye__label">{label}</div>
            <NumberFormat
                value={value == null || value === '' ? '' : value}
                className="number-format-input form-control purchase-requisition-item-form__input--editable purchase-requisition-item-kunye__input"
                thousandSeparator={true}
                decimalScale={2}
                fixedDecimalScale={false}
                placeholder="—"
                onValueChange={(values) => {
                    const nextVal = values.value === '' ? null : values.value;
                    this.setState(prev => {
                        const next = {...prev.item, [name]: nextVal};
                        // Manual max override: stop packing-driven maxStock recalc
                        if (name === 'maxStock') {
                            next.maxStockFromSettings = true;
                        }
                        return {item: next};
                    });
                }}
            />
        </div>
    );

    getCurrentUnitCost = (item) => {
        const raw = item?.originalCurrentCost ?? item?.currentCost;
        if (raw == null || raw === '') {
            return null;
        }
        const num = Number(raw);
        return Number.isNaN(num) ? null : num;
    };

    getCurrentBoxCost = (item) => {
        const unit = this.getCurrentUnitCost(item);
        if (unit == null) {
            return null;
        }
        const boxSize = Number(item?.boxSize);
        if (Number.isNaN(boxSize) || boxSize <= 0) {
            return unit;
        }
        return unit * boxSize;
    };

    getCurrentPrice = (item) => {
        const raw = item?.currentPrice ?? item?.salePrice ?? item?.price;
        if (raw == null || raw === '') {
            return null;
        }
        const num = Number(raw);
        return Number.isNaN(num) ? null : num;
    };

    renderCurrentInfoKunye = (item) => {
        const stockEditable = this.isNewProduct();
        const productName = item.longName || item.itemName;
        const showProductName = this.hasExistingProduct(item);
        return (
            <aside className="purchase-requisition-item-kunye" aria-label="Product info">
                {showProductName &&
                    <div className="purchase-requisition-item-kunye__cell purchase-requisition-item-kunye__cell--full">
                        {this.renderKunyeValue('Product Name', productName)}
                    </div>
                }
                <div className="purchase-requisition-item-kunye__grid">
                    {this.renderKunyeValue('Cur. Unit Cost', this.getCurrentUnitCost(item), {isCurrency: true})}
                    {this.renderKunyeValue('Cur. Box Cost', this.getCurrentBoxCost(item), {isCurrency: true})}
                    {this.renderKunyeValue('Cur. Price', this.getCurrentPrice(item), {isCurrency: true})}
                    {this.renderKunyeValue('Cur. Stock', item.currentStock)}
                    {this.renderKunyeValue('Rec. Qty', item.recommendedOrderAmount)}
                    {stockEditable
                        ? this.renderKunyeEditableNumber('minStock', 'Min Stock', item.minStock)
                        : this.renderKunyeValue('Min Stock', item.minStock)}
                    {stockEditable
                        ? this.renderKunyeEditableNumber('maxStock', 'Max Stock', item.maxStock)
                        : this.renderKunyeValue('Max Stock', item.maxStock)}
                </div>
            </aside>
        );
    };

    renderReturnUnitCostField(item) {
        const empty = item.currentCost == null || item.currentCost === '';
        return (
            <AvGroup>
                <Label for="returnUnitCost">Cur. Unit Cost</Label>
                <input
                    id="returnUnitCost"
                    type="text"
                    className="form-control purchase-requisition-item-form__input--readonly"
                    value={empty ? '—' : this.formatMoney(item.currentCost)}
                    disabled={true}
                    readOnly={true}
                />
            </AvGroup>
        );
    }

    getPackingMode = () => {
        if (this.props.deliveryMode) {
            return 'delivery';
        }
        if (this.props.returnMode) {
            return 'return';
        }
        return 'order';
    };

    getPackingLabels = (item) => getOrderPackingLabels(
        item?.orderUnitOfMeasure,
        this.getPackingMode()
    );

    getActualQtyLabel = (item) => this.getPackingLabels(item).total;

    getBoxQuantityLabel = (item) => this.getPackingLabels(item).unitsPerBox;

    getItemQuantityLabel = (item) => this.getPackingLabels(item).orderQty;

    getOrderUnitOfMeasureLabel = (item) => this.getPackingLabels(item).uom;

    getTotalPiecesHint = (item) => this.getPackingLabels(item).totalHint;

    getDeliveryQuantityLabel = () => 'Total Qty';

    getDamagedQuantityLabel = () => 'Rejected Qty';

    getNetQuantityLabel = () => 'Net Qty';

    renderOrderUnitOfMeasureField = (item) => {
        const disabled = this.isReadOnly();
        return (
            <AvGroup>
                <Label for="orderUnitOfMeasure">
                    {this.getOrderUnitOfMeasureLabel(item)}{!disabled && requiredMark}
                </Label>
                <AvField
                    type="select"
                    name="orderUnitOfMeasure"
                    className="form-control purchase-requisition-item-form__input--editable"
                    value={normalizeOrderUnitOfMeasure(item.orderUnitOfMeasure)}
                    onChange={this.__handleChange}
                    disabled={disabled}
                >
                    {(ORDER_UNIT_OF_MEASURE || []).map(element => (
                        <option value={element.value} key={element.value}>{element.name}</option>
                    ))}
                </AvField>
            </AvGroup>
        );
    };

    renderDeliveryBoxVisual = (item) => {
        // Packing summary / grid is desktop only.
        if (this.props.webPopup !== true) {
            return null;
        }
        const isSingle = isOrderUnitSingle(item?.orderUnitOfMeasure);
        const orderQty = Number(item?.deliveredBoxCount);
        const unitsPerBox = Number(item?.boxSize);
        if (Number.isNaN(orderQty)
            || item?.deliveredBoxCount === '' || item?.deliveredBoxCount == null
            || orderQty <= 0) {
            return null;
        }
        if (!isSingle && (Number.isNaN(unitsPerBox)
            || item?.boxSize === '' || item?.boxSize == null
            || unitsPerBox <= 0)) {
            return null;
        }

        // Case: qty × Box Qty. Single: qty only (box qty ignored for total).
        const boxCount = isSingle ? 1 : Math.floor(orderQty);
        const perBox = isSingle ? Math.floor(orderQty) : Math.floor(unitsPerBox);
        const total = isSingle ? Math.floor(orderQty) : (Math.floor(orderQty) * Math.floor(unitsPerBox));
        const damagedRaw = Number(item?.damagedQuantity);
        const damagedCount = Number.isNaN(damagedRaw)
            || item?.damagedQuantity === ''
            || item?.damagedQuantity == null
            ? 0
            : Math.max(0, Math.min(Math.floor(damagedRaw), total));
        const netCount = total - damagedCount;
        const expanded = this.state.packingVisualExpanded === true;
        const showGrid = expanded;

        const maxBoxes = 12;
        const maxUnits = 24;
        const shownBoxes = Math.min(boxCount, maxBoxes);
        const shownUnits = Math.min(perBox, maxUnits);
        const cols = Math.min(6, Math.max(2, Math.ceil(Math.sqrt(Math.max(shownUnits, 1)))));
        const damagedStartIndex = total - damagedCount;

        return (
            <div className={`dn-box-visual${showGrid ? '' : ' dn-box-visual--collapsed'}`}>
                <div className="dn-box-visual__header">
                    <div className="dn-box-visual__summary">
                        {isSingle
                            ? (
                                <>
                                    <strong>{total}</strong> total pieces
                                    <span className="dn-box-visual__sep">·</span>
                                    Single
                                </>
                            )
                            : (
                                <>
                                    <strong>{boxCount}</strong> case{boxCount === 1 ? '' : 's'}
                                    <span className="dn-box-visual__times">×</span>
                                    <strong>{perBox}</strong> per case
                                    <span className="dn-box-visual__eq">=</span>
                                    <strong>{total}</strong> total pieces
                                </>
                            )
                        }
                        {damagedCount > 0 &&
                            <>
                                <span className="dn-box-visual__sep">·</span>
                                <strong className="dn-box-visual__damaged-text">{damagedCount}</strong> rejected
                                <span className="dn-box-visual__sep">·</span>
                                <strong>{netCount}</strong> net
                            </>
                        }
                    </div>
                    <button
                        type="button"
                        className="dn-box-visual__toggle"
                        onClick={() => this.setState(prev => ({packingVisualExpanded: !prev.packingVisualExpanded}))}
                    >
                        {showGrid ? 'Hide packing' : 'Show packing'}
                    </button>
                </div>
                {showGrid &&
                    <>
                        {damagedCount > 0 &&
                            <div className="dn-box-visual__legend">
                                <span className="dn-box-visual__legend-item">
                                    <span className="dn-box-visual__unit"/> OK
                                </span>
                                <span className="dn-box-visual__legend-item">
                                    <span className="dn-box-visual__unit dn-box-visual__unit--damaged"/> Rejected
                                </span>
                            </div>
                        }
                        <div className="dn-box-visual__boxes" aria-hidden="true">
                            {Array.from({length: shownBoxes}).map((_, boxIndex) => (
                                <div className="dn-box-visual__box" key={`box-${boxIndex}`}>
                                    <div
                                        className="dn-box-visual__units"
                                        style={{gridTemplateColumns: `repeat(${cols}, 1fr)`}}
                                    >
                                        {Array.from({length: shownUnits}).map((__, unitIndex) => {
                                            const globalIndex = boxIndex * perBox + unitIndex;
                                            const isDamaged = damagedCount > 0 && globalIndex >= damagedStartIndex;
                                            return (
                                                <span
                                                    className={`dn-box-visual__unit${isDamaged ? ' dn-box-visual__unit--damaged' : ''}`}
                                                    key={`u-${boxIndex}-${unitIndex}`}
                                                />
                                            );
                                        })}
                                    </div>
                                    <div className="dn-box-visual__box-label">{perBox}</div>
                                </div>
                            ))}
                            {boxCount > maxBoxes &&
                                <div className="dn-box-visual__more">+{boxCount - maxBoxes} more boxes</div>
                            }
                        </div>
                        {perBox > maxUnits &&
                            <div className="dn-box-visual__note">
                                Showing {maxUnits} of {perBox} units per box
                            </div>
                        }
                    </>
                }
            </div>
        );
    };

    getDeliveryQuantity = (item) => {
        return calculateActualOrderAmount(
            item?.deliveredBoxCount,
            item?.boxSize,
            item?.orderUnitOfMeasure
        );
    };

    getNetQuantity = (item) => {
        const deliveryQty = this.getDeliveryQuantity(item);
        if (deliveryQty == null) {
            return null;
        }
        const damaged = Number(item?.damagedQuantity);
        const damagedValue = Number.isNaN(damaged) || item?.damagedQuantity === '' || item?.damagedQuantity == null
            ? 0
            : damaged;
        return deliveryQty - damagedValue;
    };

    calculateDeliveryQty = (boxes, boxQty, orderUnitOfMeasure) => {
        return calculateActualOrderAmount(boxes, boxQty, orderUnitOfMeasure);
    };

    updateDeliveryCalcFields = (patch) => {
        this.setState(prev => {
            const item = {...prev.item, ...patch};
            if (Object.prototype.hasOwnProperty.call(patch, 'orderUnitOfMeasure')) {
                item.orderUnitOfMeasure = normalizeOrderUnitOfMeasure(item.orderUnitOfMeasure);
            }
            const calculated = this.calculateDeliveryQty(
                item.deliveredBoxCount, item.boxSize, item.orderUnitOfMeasure
            );
            if (calculated != null) {
                item.actualOrderAmount = calculated;
            }
            if (Object.prototype.hasOwnProperty.call(patch, 'damagedQuantity')) {
                const damagedNumber = Number(item.damagedQuantity);
                const damagedEmpty = item.damagedQuantity == null || String(item.damagedQuantity).trim() === ''
                    || Number.isNaN(damagedNumber) || damagedNumber <= 0;
                if (damagedEmpty) {
                    item.reasonCode = null;
                    item.expiryDate = null;
                    item.description = null;
                }
            }
            if (Object.prototype.hasOwnProperty.call(patch, 'damagedQuantity')
                && item.damagedQuantity != null
                && String(item.damagedQuantity).trim() !== '') {
                const damagedNumber = Number(item.damagedQuantity);
                const totalQty = calculated != null ? calculated : this.getDeliveryQuantity(item);
                if (!Number.isNaN(damagedNumber) && totalQty != null && damagedNumber > totalQty) {
                    toast.warn('Rejected Qty cannot be greater than Total Qty');
                    item.damagedQuantity = prev.item.damagedQuantity;
                    item.reasonCode = prev.item.reasonCode;
                    item.expiryDate = prev.item.expiryDate;
                    item.description = prev.item.description;
                }
            } else if (calculated != null
                && item.damagedQuantity != null
                && String(item.damagedQuantity).trim() !== '') {
                const damagedNumber = Number(item.damagedQuantity);
                if (!Number.isNaN(damagedNumber) && damagedNumber > calculated) {
                    toast.warn('Rejected Qty cannot be greater than Total Qty');
                    item.damagedQuantity = null;
                    item.reasonCode = null;
                    item.expiryDate = null;
                    item.description = null;
                }
            }
            const nextState = {item};
            return nextState;
        });
    };

    __handleWasteReasonChange = (e) => {
        if (this.isReadOnly()) {
            return;
        }
        const reasonCode = e.target.value || null;
        this.setState(prev => ({
            item: {
                ...prev.item,
                reasonCode,
                expiryDate: reasonCode === 'EXPIRED' ? prev.item.expiryDate : null
            }
        }));
    };

    hasWasteQuantity = (item) => {
        const damaged = Number(item?.damagedQuantity);
        return item?.damagedQuantity != null
            && String(item.damagedQuantity).trim() !== ''
            && !Number.isNaN(damaged)
            && damaged > 0;
    };

    updateOrderCalcFields = (patch) => {
        this.setState(prev => {
            const item = {...prev.item, ...patch};
            item.orderUnitOfMeasure = normalizeOrderUnitOfMeasure(item.orderUnitOfMeasure);
            if (item.deliveredBoxCount != null && item.deliveredBoxCount !== '') {
                const boxes = Number(item.deliveredBoxCount);
                // Case orders are whole cases; Single allows any positive pieces count.
                if (!isOrderUnitSingle(item.orderUnitOfMeasure)
                    && !Number.isNaN(boxes) && !Number.isInteger(boxes)) {
                    item.deliveredBoxCount = Math.ceil(boxes);
                }
            }
            const calculated = calculateActualOrderAmount(
                item.deliveredBoxCount, item.boxSize, item.orderUnitOfMeasure
            );
            if (calculated != null) {
                item.actualOrderAmount = calculated;
            }
            // New product: if maxStock was not fixed from settings, keep it = order total (invoice-like)
            if (GeneralUtils.isNullOrEmpty(item.itemBssId)
                && item.barcodeIsSearched === true
                && !item.maxStockFromSettings) {
                item.maxStock = this.calcDefaultMaxStock(item);
            }
            return {item};
        });
    };

    renderReadOnlyValue = (label, value, options = {}) => {
        let displayValue;
        if (options.isCurrency) {
            const num = Number(value);
            displayValue = !Number.isNaN(num) ? `£${num.toFixed(2)}` : '£0.00';
        } else if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
            displayValue = '—';
        } else {
            displayValue = value;
        }
        return (
            <AvGroup>
                <Label>{label}</Label>
                <input
                    type="text"
                    className="form-control purchase-requisition-item-form__input--readonly"
                    value={displayValue}
                    disabled={true}
                    readOnly={true}
                />
            </AvGroup>
        );
    };

    getUnitDisplayName = (unitValue) => {
        if (unitValue == null || unitValue === '') {
            return null;
        }
        const match = (PRODUCT_UNIT || []).find(element =>
            String(element.value) === String(unitValue) || String(element.name) === String(unitValue)
        );
        return match ? match.name : unitValue;
    };

    renderProductImage = (item) => {
        const hasImage = !GeneralUtils.isNullOrEmpty(item?.webImagePath);
        return (
            <div className={`pr-item-product-image${hasImage ? '' : ' pr-item-product-image--empty'}`}>
                {hasImage
                    ? <img src={WEB_IMAGE_SERVER_URL + item.webImagePath} alt={item.longName || item.itemName || 'Product'}/>
                    : <span>No image</span>
                }
            </div>
        );
    };

    renderActualOrderAmountField = (item, required = true) => {
        const tone = getActualOrderAmountTone(item);
        const toneClass = tone ? ` pr-actual-order-amount--${tone}` : '';
        // Live total: Case = Order Qty × Box Qty; Single = Order Qty.
        const isPrOrder = !this.props.deliveryMode && !this.props.returnMode;
        const liveTotal = isPrOrder
            ? calculateActualOrderAmount(
                item.deliveredBoxCount, item.boxSize, item.orderUnitOfMeasure
            )
            : null;
        const displayValue = liveTotal != null ? liveTotal : item.actualOrderAmount;
        const empty = displayValue == null || displayValue === '';
        return (
            <AvGroup>
                <Label for="actualOrderAmount" className="purchase-requisition-item-form__label--nowrap">
                    {this.getActualQtyLabel(item)}
                    {required && requiredMark}
                </Label>
                <NumberFormat
                    value={empty ? '' : displayValue}
                    className={`number-format-input form-control purchase-requisition-item-form__input--readonly pr-actual-order-amount${toneClass}`}
                    thousandSeparator={true}
                    decimalScale={2}
                    fixedDecimalScale={false}
                    placeholder="—"
                    disabled={true}
                />
                {isPrOrder && this.getTotalPiecesHint(item) && (
                    <div className="text-muted" style={{fontSize: 11, marginTop: 4}}>
                        {this.getTotalPiecesHint(item)}
                    </div>
                )}
            </AvGroup>
        );
    };

    __handleExpiryDateChange = (e) => {
        if (this.isReadOnly()) {
            return;
        }
        const raw = e.target.parsedValue !== undefined ? e.target.parsedValue : e.target.value;
        let expiryDate = null;
        if (raw !== '' && raw != null) {
            const parsed = dayjs(Number(raw));
            expiryDate = parsed.isValid() ? parsed.format('DD/MM/YYYY') : null;
        }
        this.setState(prev => ({
            item: {...prev.item, expiryDate}
        }));
    };

    getExpiryDateInputValue = (item) => {
        if (GeneralUtils.isNullOrEmpty(item?.expiryDate)) {
            return '';
        }
        const parsed = dayjs(item.expiryDate, 'DD/MM/YYYY');
        return parsed.isValid() ? parsed.valueOf() : '';
    };

    renderDeliveryNumberField = (name, label, required = false, onValueChange, fieldDisabled = false) => {
        const disabled = this.isFieldDisabled(fieldDisabled);
        const readonlyClass = disabled
            ? ' purchase-requisition-item-form__input--readonly'
            : ' purchase-requisition-item-form__input--editable';
        const rawValue = this.state.item[name];
        if (disabled && (rawValue == null || rawValue === '')) {
            return (
                <AvGroup>
                    <Label for={name} className="purchase-requisition-item-form__label--nowrap">
                        {label}
                        {required && !this.isReadOnly() && requiredMark}
                    </Label>
                    <input
                        type="text"
                        className={`form-control${readonlyClass}`}
                        value="—"
                        disabled={true}
                        readOnly={true}
                    />
                </AvGroup>
            );
        }
        return (
            <AvGroup>
                <Label for={name} className="purchase-requisition-item-form__label--nowrap">
                    {label}
                    {required && !disabled && requiredMark}
                </Label>
                <NumberFormat
                    value={rawValue}
                    className={`number-format-input form-control${readonlyClass}`}
                    thousandSeparator={true}
                    decimalScale={2}
                    fixedDecimalScale={false}
                    onValueChange={(values) => {
                        if (disabled) {
                            return;
                        }
                        if (onValueChange) {
                            onValueChange(values.value);
                            return;
                        }
                        this.setState(prev => ({
                            item: {...prev.item, [name]: values.value}
                        }));
                    }}
                    disabled={disabled}
                />
            </AvGroup>
        );
    };

    renderDeliveryFormBody(item) {
        const deliveryQty = this.getDeliveryQuantity(item);
        const netQty = this.getNetQuantity(item);
        return (
            <div className="purchase-requisition-item-form__shell">
                <div className="purchase-requisition-item-form__layout">
                    <div className="purchase-requisition-item-form__main">
                        <div className="purchase-requisition-item-form__grid">
                            <div className="purchase-requisition-item-form__section">
                                <div className="purchase-requisition-item-form__section-title">Identification</div>
                                <div className="purchase-requisition-item-form__row purchase-requisition-item-form__row--identity">
                                    <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--12">
                                        {this.renderBarcodeField(item)}
                                    </div>
                                </div>
                                {this.shouldShowProductNameUnderBarcode(item) &&
                                    <div className="purchase-requisition-item-form__row purchase-requisition-item-form__row--identity">
                                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--12">
                                            {this.renderProductNameField(item, this.props.webPopup === true)}
                                        </div>
                                    </div>
                                }
                            </div>

                            <div className="purchase-requisition-item-form__section">
                                <div className="purchase-requisition-item-form__section-title">Quantity</div>
                                <div className="purchase-requisition-item-form__row purchase-requisition-item-form__row--packaging">
                                    <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--3">
                                        {this.renderOrderUnitOfMeasureField(item)}
                                    </div>
                                    <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--3">
                                        {this.renderDeliveryNumberField(
                                            'boxSize',
                                            this.getBoxQuantityLabel(item),
                                            !isOrderUnitSingle(item.orderUnitOfMeasure),
                                            (value) => this.updateDeliveryCalcFields({boxSize: value}),
                                            this.isBoxSizeFieldDisabled(item)
                                                || isOrderUnitSingle(item.orderUnitOfMeasure)
                                        )}
                                    </div>
                                    <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--3">
                                        {this.renderDeliveryNumberField(
                                            'deliveredBoxCount',
                                            this.getItemQuantityLabel(item),
                                            true,
                                            (value) => this.updateDeliveryCalcFields({deliveredBoxCount: value})
                                        )}
                                    </div>
                                    <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--3">
                                        {this.renderReadOnlyValue(this.getDeliveryQuantityLabel(), deliveryQty)}
                                    </div>
                                </div>
                                <div className="purchase-requisition-item-form__row purchase-requisition-item-form__row--packaging">
                                    <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                                        {this.renderDeliveryNumberField(
                                            'damagedQuantity',
                                            this.getDamagedQuantityLabel(),
                                            false,
                                            (value) => this.updateDeliveryCalcFields({damagedQuantity: value})
                                        )}
                                    </div>
                                    <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                                        {this.renderReadOnlyValue(this.getNetQuantityLabel(), netQty)}
                                    </div>
                                    {this.hasWasteQuantity(item) &&
                                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                                            <AvGroup>
                                                <Label for="reasonCode" className="purchase-requisition-item-form__label--nowrap">
                                                    Rejected Reason
                                                    {!this.isReadOnly() && requiredMark}
                                                </Label>
                                                <AvField
                                                    type="select"
                                                    name="reasonCode"
                                                    className={this.isReadOnly()
                                                        ? 'form-control purchase-requisition-item-form__input--readonly'
                                                        : 'form-control purchase-requisition-item-form__input--editable'}
                                                    value={item.reasonCode || ''}
                                                    onChange={this.__handleWasteReasonChange}
                                                    disabled={this.isReadOnly()}
                                                >
                                                    <option value="" key=""/>
                                                    {DELIVERY_WASTE_REASON_CODE.map(element => (
                                                        <option value={element.value} key={element.value}>{element.name}</option>
                                                    ))}
                                                </AvField>
                                            </AvGroup>
                                        </div>
                                    }
                                </div>
                                {this.hasWasteQuantity(item) && item.reasonCode === 'EXPIRED' &&
                                    <div className="purchase-requisition-item-form__row purchase-requisition-item-form__row--packaging">
                                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                                            <AvGroup>
                                                <Label for="expiryDate" className="purchase-requisition-item-form__label--nowrap">
                                                    Expiry Date
                                                    {!this.isReadOnly() && requiredMark}
                                                </Label>
                                                <DateInput
                                                    name="expiryDate"
                                                    format="DD/MM/YYYY"
                                                    value={this.getExpiryDateInputValue(item)}
                                                    onChange={this.__handleExpiryDateChange}
                                                    disabled={this.isReadOnly()}
                                                />
                                            </AvGroup>
                                        </div>
                                    </div>
                                }
                                {this.hasWasteQuantity(item) &&
                                    <div className="purchase-requisition-item-form__row">
                                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--12">
                                            <AvGroup>
                                                <Label for="description">
                                                    Rejected Description
                                                    {!this.isReadOnly() && item.reasonCode === 'OTHER' && requiredMark}
                                                </Label>
                                                <AvField
                                                    type="text"
                                                    name="description"
                                                    maxLength={50}
                                                    className={this.isReadOnly()
                                                        ? 'form-control purchase-requisition-item-form__input--readonly'
                                                        : 'form-control purchase-requisition-item-form__input--editable'}
                                                    value={item.description || ''}
                                                    onChange={this.__handleChange}
                                                    disabled={this.isReadOnly()}
                                                    placeholder={item.reasonCode === 'OTHER' ? 'Required for Other' : ''}
                                                />
                                            </AvGroup>
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                    {this.renderCurrentInfoKunye(item)}
                </div>

                <div className="purchase-requisition-item-form__packing">
                    {this.renderDeliveryBoxVisual(item)}
                </div>
            </div>
        );
    }

    renderWebFormBody(item) {
        const isWeb = true;
        const isDelivery = this.props.deliveryMode === true;
        if (isDelivery) {
            return this.renderDeliveryFormBody(item);
        }
        const newProduct = this.isNewProduct();
        return (
            <div className="purchase-requisition-item-form__shell">
                <div className="purchase-requisition-item-form__layout">
                    <div className="purchase-requisition-item-form__main">
                        <div className="purchase-requisition-item-form__grid">
                            <div className="purchase-requisition-item-form__section">
                                <div className="purchase-requisition-item-form__section-title">Identification</div>
                                <div className="purchase-requisition-item-form__row purchase-requisition-item-form__row--identity">
                                    <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--12">
                                        {this.renderBarcodeField(item)}
                                    </div>
                                </div>
                                {newProduct &&
                                    <>
                                        <div className="purchase-requisition-item-form__row purchase-requisition-item-form__row--identity">
                                            <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--12">
                                                {this.renderProductNameField(item, isWeb)}
                                            </div>
                                        </div>
                                        <div className="purchase-requisition-item-form__row purchase-requisition-item-form__row--identity">
                                            <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                                                {this.renderItemSizeField(item)}
                                            </div>
                                            <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                                                {this.renderItemUnitField(item)}
                                            </div>
                                            <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                                                {this.renderDepartmentField(item)}
                                            </div>
                                        </div>
                                        <div className="purchase-requisition-item-form__row purchase-requisition-item-form__row--identity">
                                            <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--6">
                                                {this.renderTaxField(item)}
                                            </div>
                                            <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--6">
                                                {this.renderSalePriceField(item)}
                                            </div>
                                        </div>
                                    </>
                                }
                            </div>

                            <div className="purchase-requisition-item-form__section">
                                <div className="purchase-requisition-item-form__section-title">Packaging &amp; Amount</div>
                                <div className="purchase-requisition-item-form__row purchase-requisition-item-form__row--packaging">
                                    <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                                        {this.renderOrderUnitOfMeasureField(item)}
                                    </div>
                                    <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                                        {this.renderDeliveryNumberField(
                                            'boxSize',
                                            this.getBoxQuantityLabel(item),
                                            !isOrderUnitSingle(item.orderUnitOfMeasure),
                                            (value) => this.updateOrderCalcFields({boxSize: value}),
                                            this.isBoxSizeFieldDisabled(item)
                                        )}
                                    </div>
                                    <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                                        {this.renderDeliveryNumberField(
                                            'deliveredBoxCount',
                                            this.getItemQuantityLabel(item),
                                            true,
                                            (value) => this.updateOrderCalcFields({deliveredBoxCount: value}),
                                            false
                                        )}
                                    </div>
                                </div>
                                {!isOrderUnitSingle(item.orderUnitOfMeasure) &&
                                    <div className="purchase-requisition-item-form__row purchase-requisition-item-form__row--packaging">
                                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--5">
                                            {this.renderActualOrderAmountField(item, true)}
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                    {this.renderCurrentInfoKunye(item)}
                </div>

                <div className="purchase-requisition-item-form__packing">
                    {this.renderDeliveryBoxVisual(item)}
                </div>

                <div className="purchase-requisition-item-form__section">
                    <div className="purchase-requisition-item-form__section-title">Supplier</div>
                    {this.renderWebSupplierFields(item)}
                </div>
            </div>
        );
    }

    renderReturnFormBody(item) {
        const disabled = this.isReadOnly();
        const returnQty = this.getDeliveryQuantity(item);
        return (
            <div className="purchase-requisition-item-form__layout">
                <div className="purchase-requisition-item-form__main">
                    <div className="purchase-requisition-item-form__grid">
                        <div className="purchase-requisition-item-form__section">
                            <div className="purchase-requisition-item-form__section-title">Identification</div>
                            <div className="purchase-requisition-item-form__row purchase-requisition-item-form__row--identity">
                                <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--12">
                                    {this.renderBarcodeField(item)}
                                </div>
                            </div>
                            {this.shouldShowProductNameUnderBarcode(item) &&
                                <div className="purchase-requisition-item-form__row purchase-requisition-item-form__row--identity">
                                    <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--12">
                                        {this.renderProductNameField(item, this.props.webPopup === true)}
                                    </div>
                                </div>
                            }
                        </div>

                        <div className="purchase-requisition-item-form__section">
                            <div className="purchase-requisition-item-form__section-title">Return Note</div>
                            <div className="purchase-requisition-item-form__row purchase-requisition-item-form__row--packaging">
                                <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--3">
                                    {this.renderOrderUnitOfMeasureField(item)}
                                </div>
                                <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--3">
                                    {this.renderDeliveryNumberField(
                                        'boxSize',
                                        this.getBoxQuantityLabel(item),
                                        !isOrderUnitSingle(item.orderUnitOfMeasure),
                                        (value) => this.updateDeliveryCalcFields({boxSize: value}),
                                        this.isBoxSizeFieldDisabled(item)
                                            || isOrderUnitSingle(item.orderUnitOfMeasure)
                                    )}
                                </div>
                                <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--3">
                                    {this.renderDeliveryNumberField(
                                        'deliveredBoxCount',
                                        this.getItemQuantityLabel(item),
                                        true,
                                        (value) => this.updateDeliveryCalcFields({deliveredBoxCount: value})
                                    )}
                                </div>
                                <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--3">
                                    {this.renderReadOnlyValue('Return Quantity', returnQty)}
                                </div>
                            </div>
                            <div className="purchase-requisition-item-form__row purchase-requisition-item-form__row--packaging">
                                <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                                    <AvGroup>
                                        <Label for="reasonCode" className="purchase-requisition-item-form__label--nowrap">
                                            Reason Code
                                            {!disabled && requiredMark}
                                        </Label>
                                        <AvField
                                            type="select"
                                            name="reasonCode"
                                            className={disabled
                                                ? 'form-control purchase-requisition-item-form__input--readonly'
                                                : 'form-control purchase-requisition-item-form__input--editable'}
                                            value={item.reasonCode || ''}
                                            onChange={this.__handleWasteReasonChange}
                                            disabled={disabled}
                                        >
                                            <option value="" key=""/>
                                            {RETURN_REASON_CODE.map(element => (
                                                <option value={element.value} key={element.value}>{element.name}</option>
                                            ))}
                                        </AvField>
                                    </AvGroup>
                                </div>
                                <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--8">
                                    <AvGroup>
                                        <Label for="description">
                                            Return Description
                                            {!disabled && item.reasonCode === 'OTHER' && requiredMark}
                                        </Label>
                                        <AvField
                                            type="text"
                                            name="description"
                                            maxLength={50}
                                            className={disabled
                                                ? 'form-control purchase-requisition-item-form__input--readonly'
                                                : 'form-control purchase-requisition-item-form__input--editable'}
                                            value={item.description || ''}
                                            onChange={this.__handleChange}
                                            disabled={disabled}
                                            placeholder={item.reasonCode === 'OTHER' ? 'Required for Other' : ''}
                                        />
                                    </AvGroup>
                                </div>
                            </div>
                            {item.reasonCode === 'EXPIRED' &&
                                <div className="purchase-requisition-item-form__row purchase-requisition-item-form__row--packaging">
                                    <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                                        <AvGroup>
                                            <Label for="expiryDate" className="purchase-requisition-item-form__label--nowrap">
                                                Expiry Date
                                                {!disabled && requiredMark}
                                            </Label>
                                            <DateInput
                                                name="expiryDate"
                                                format="DD/MM/YYYY"
                                                value={this.getExpiryDateInputValue(item)}
                                                onChange={this.__handleExpiryDateChange}
                                                disabled={disabled}
                                            />
                                        </AvGroup>
                                    </div>
                                </div>
                            }
                        </div>

                        <div className="purchase-requisition-item-form__section">
                            <div className="purchase-requisition-item-form__section-title">Supplier</div>
                            {this.renderWebSupplierFields(item)}
                        </div>
                    </div>
                </div>
                {this.renderCurrentInfoKunye(item)}
            </div>
        );
    }

    /** Mobile: same rules as web (single column). */
    renderMobileFormBody(item) {
        const isWeb = false;
        return (
            <div className="purchase-requisition-item-form__grid purchase-requisition-item-form__grid--mobile">
                    <div className="purchase-requisition-item-form__section">
                    <div className="purchase-requisition-item-form__section-title">Identification</div>
                    <div className="purchase-requisition-item-form__row">
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--12">
                            {this.renderBarcodeField(item)}
                        </div>
                    </div>
                    {this.shouldShowProductNameUnderBarcode(item) &&
                        <div className="purchase-requisition-item-form__row">
                            <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--12">
                                {this.renderProductNameField(item, isWeb)}
                            </div>
                        </div>
                    }
                    {this.isNewProduct() &&
                        <>
                            <div className="purchase-requisition-item-form__row">
                                <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                                    {this.renderItemSizeField(item)}
                                </div>
                                <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                                    {this.renderItemUnitField(item)}
                                </div>
                                <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                                    {this.renderDepartmentField(item)}
                                </div>
                            </div>
                            <div className="purchase-requisition-item-form__row">
                                <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--6">
                                    {this.renderTaxField(item)}
                                </div>
                                <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--6">
                                    {this.renderSalePriceField(item)}
                                </div>
                            </div>
                        </>
                    }
                </div>

                {this.renderCurrentInfoKunye(item)}

                <div className="purchase-requisition-item-form__section">
                    <div className="purchase-requisition-item-form__section-title">Packaging &amp; Amount</div>
                    <div className="purchase-requisition-item-form__row">
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--12">
                            {this.renderOrderUnitOfMeasureField(item)}
                        </div>
                    </div>
                    <div className="purchase-requisition-item-form__row">
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--6">
                            {this.renderDeliveryNumberField(
                                'boxSize',
                                this.getBoxQuantityLabel(item),
                                !isOrderUnitSingle(item.orderUnitOfMeasure),
                                (value) => this.updateOrderCalcFields({boxSize: value}),
                                this.isBoxSizeFieldDisabled(item)
                            )}
                        </div>
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--6">
                            {this.renderDeliveryNumberField(
                                'deliveredBoxCount',
                                this.getItemQuantityLabel(item),
                                true,
                                (value) => this.updateOrderCalcFields({deliveredBoxCount: value})
                            )}
                        </div>
                    </div>
                    {!isOrderUnitSingle(item.orderUnitOfMeasure) &&
                        <div className="purchase-requisition-item-form__row">
                            <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--12">
                                {this.renderActualOrderAmountField(item, true)}
                            </div>
                        </div>
                    }
                </div>

                <div className="purchase-requisition-item-form__section">
                    <div className="purchase-requisition-item-form__section-title">Supplier</div>
                    <div className="purchase-requisition-item-form__row">
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--12">
                            {this.renderSupplierField(item, isWeb)}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderFormBody(item) {
        const isWeb = this.props.webPopup === true;
        if (this.props.deliveryMode) {
            return this.renderDeliveryFormBody(item);
        }
        if (this.props.returnMode) {
            return this.renderReturnFormBody(item);
        }
        if (isWeb) {
            return this.renderWebFormBody(item);
        }
        return this.renderMobileFormBody(item);
    }

    render() {
        const item = this.state.item || {};
        const formBody = this.renderFormBody(item);

        if (this.props.webPopup) {
            return (
                <div className="purchase-requisition-item-form purchase-requisition-item-form--web">
                    {formBody}
                    {this.renderFooter()}
                </div>
            );
        }

        return (
            <div className="purchase-requisition-item-form purchase-requisition-item-form--mobile">
                {formBody}
                {this.renderFooter()}
            </div>
        );
    }
}
